import React, { useState, useRef, useEffect } from 'react';
import styles from "./DLTest.module.css";
import { ChevronRight, CheckCircle, XCircle, BookOpen, Clock, Award, Download, Share2 } from 'lucide-react';
import logoImage from '../../../assets/rtoxpertLogo.png';

const testCollections = {
  english: [
    // Easy 1
    {
      id: 1,
      title: "Traffic Basics",
      difficulty: "easy",
      icon: "🚦",
      totalQuestions: 20,
      questions: [
        {
          id: 1,
          question: "What does a red traffic light mean?",
          options: [
            "Proceed with caution",
            "Stop completely",
            "Speed up",
            "Sound your horn"
          ],
          correctAnswer: 1,
          explanation: "Red means stop. Always halt your vehicle at a red traffic light."
        },
        {
          id: 2,
          question: "What is the minimum age to get a learner's license for a car in India?",
          options: ["16 years", "18 years", "20 years", "21 years"],
          correctAnswer: 1,
          explanation: "You must be 18 years or older to obtain a learner’s license for a car."
        },
        {
          id: 3,
          question: "What does a green traffic light indicate?",
          options: ["Stop", "Go", "Get ready", "Slow down"],
          correctAnswer: 1,
          explanation: "Green means you may go, provided the way is clear."
        },
        {
          id: 4,
          question: "Which side should you overtake from in India?",
          options: ["Right side", "Left side", "Any side", "Overtaking is not allowed"],
          correctAnswer: 0,
          explanation: "In India, overtaking is done from the right."
        },
        {
          id: 5,
          question: "Which of the following is a mandatory road sign?",
          options: [
            "Speed limit",
            "Give way",
            "Hospital ahead",
            "No parking"
          ],
          correctAnswer: 0,
          explanation: "Speed limit signs are mandatory."
        },
        {
          id: 6,
          question: "On a roundabout, who has the right of way?",
          options: [
            "Vehicles entering",
            "Vehicles already on the roundabout",
            "Pedestrians",
            "Cyclists"
          ],
          correctAnswer: 1,
          explanation: "Vehicles already on the roundabout have the right of way."
        },
        {
          id: 7,
          question: "What should you do before taking a turn?",
          options: [
            "Sound the horn and speed up",
            "Slow down and check mirrors",
            "Just turn",
            "None of the above"
          ],
          correctAnswer: 1,
          explanation: "Always slow down and check mirrors before turning."
        },
        {
          id: 8,
          question: "Yellow line in the center of the road means:",
          options: [
            "Parking allowed",
            "Overtaking allowed",
            "Overtaking not allowed",
            "No stopping"
          ],
          correctAnswer: 2,
          explanation: "A continuous yellow line prohibits overtaking or crossing."
        },
        {
          id: 9,
          question: "Seatbelt is:",
          options: [
            "Optional",
            "Required only for front seat",
            "Mandatory for all passengers",
            "Not necessary in city"
          ],
          correctAnswer: 2,
          explanation: "Wearing a seatbelt is mandatory for all passengers."
        },
        {
          id: 10,
          question: "If an emergency vehicle approaches with siren, you should:",
          options: [
            "Accelerate away",
            "Ignore",
            "Move to left and stop",
            "Stop in the middle of the road"
          ],
          correctAnswer: 2,
          explanation: "Move to the left and stop to allow it to pass."
        },
        {
          id: 11,
          question: "What must you carry while driving?",
          options: [
            "Driving license",
            "A cellphone",
            "Chocolates",
            "Sunglasses"
          ],
          correctAnswer: 0,
          explanation: "Carrying your driving license is mandatory."
        },
        {
          id: 12,
          question: "The horn should be used:",
          options: [
            "At hospitals",
            "To alert others when necessary",
            "While overtaking in silent zones",
            "Continuously in heavy traffic"
          ],
          correctAnswer: 1,
          explanation: "Only use when necessary to alert others."
        },
        {
          id: 13,
          question: "Pedestrian crossings (zebra crossings) are for:",
          options: [
            "Stopping cars",
            "Allowing pedestrians to cross",
            "Parking vehicles",
            "Cyclists only"
          ],
          correctAnswer: 1,
          explanation: "They are for pedestrian crossing—give way to them."
        },
        {
          id: 14,
          question: "Wearing a helmet is compulsory for:",
          options: [
            "Car drivers",
            "Truck drivers",
            "Two-wheeler riders",
            "Bus conductors"
          ],
          correctAnswer: 2,
          explanation: "Helmets are compulsory for all two-wheeler riders."
        },
        {
          id: 15,
          question: "Double solid lines on the road mean:",
          options: [
            "Overtaking allowed",
            "Do not cross",
            "Speed up",
            "Honking needed"
          ],
          correctAnswer: 1,
          explanation: "Double solid lines mean do not cross or overtake."
        },
        {
          id: 16,
          question: "What is the primary use of rear-view mirrors?",
          options: [
            "Make-up",
            "Checking traffic behind",
            "Blocking sunlight",
            "Looking at passengers"
          ],
          correctAnswer: 1,
          explanation: "Rear-view mirrors are to see traffic behind you."
        },
        {
          id: 17,
          question: "Drunk driving penalty may include:",
          options: [
            "A fine",
            "Imprisonment",
            "License suspension",
            "All of these"
          ],
          correctAnswer: 3,
          explanation: "All of these are potential penalties for drunk driving."
        },
        {
          id: 18,
          question: "You should NOT use the horn:",
          options: [
            "At night in residential areas",
            "While overtaking",
            "To alert about danger",
            "None of these"
          ],
          correctAnswer: 0,
          explanation: "Do not use horn in residential areas at night."
        },
        {
          id: 19,
          question: "The handbrake is primarily for:",
          options: [
            "Speeding",
            "Parking",
            "Turning",
            "Overtaking"
          ],
          correctAnswer: 1,
          explanation: "Handbrake is used to keep the vehicle stationary while parked."
        },
        {
          id: 20,
          question: "What should you do if a school bus stops on the road?",
          options: [
            "Sound horn to hurry up",
            "Stop until children move away",
            "Overtake quickly",
            "Ignore and move"
          ],
          correctAnswer: 1,
          explanation: "Always stop if a school bus stops and children are crossing."
        }
      ]
    },
    // Easy 2
    {
      id: 2,
      title: "Basic Rules & Signs",
      difficulty: "easy",
      icon: "📙",
      totalQuestions: 20,
      questions: [
        {
          id: 1,
          question: "What does a stop sign mean?",
          options: [
            "Yield",
            "Go",
            "Stop and proceed only when safe",
            "Accelerate"
          ],
          correctAnswer: 2,
          explanation: "A stop sign means you must stop completely before proceeding."
        },
        {
          id: 2,
          question: "What is the meaning of a 'No Parking' sign?",
          options: [
            "No stopping",
            "No entry",
            "Cannot park at that location",
            "Speed limit"
          ],
          correctAnswer: 2,
          explanation: "No Parking means you must not park your vehicle in the specified area."
        },
        {
          id: 3,
          question: "What is the minimum distance to maintain behind a moving vehicle?",
          options: [
            "At least 10 metres",
            "1 metre",
            "1 car length",
            "Any distance"
          ],
          correctAnswer: 0,
          explanation: "Maintain at least 10 metres gap for safety."
        },
        {
          id: 4,
          question: "A triangular red-bordered sign generally indicates:",
          options: [
            "Mandatory sign",
            "Cautionary sign",
            "Informative sign",
            "None"
          ],
          correctAnswer: 1,
          explanation: "Triangles with red border are cautionary signs."
        },
        {
          id: 5,
          question: "Overtaking is prohibited at:",
          options: [
            "Curves",
            "Bridges",
            "Road intersections",
            "All of these"
          ],
          correctAnswer: 3,
          explanation: "Overtaking is prohibited at all these locations for safety."
        },
        {
          id: 6,
          question: "A flashing yellow traffic signal means:",
          options: [
            "Stop completely",
            "Proceed with caution",
            "Speed quickly",
            "Sound horn"
          ],
          correctAnswer: 1,
          explanation: "A flashing yellow means proceed cautiously."
        },
        {
          id: 7,
          question: "Pedestrian crossing is marked with:",
          options: [
            "Cat eyes",
            "White stripes",
            "Red triangle",
            "Barriers"
          ],
          correctAnswer: 1,
          explanation: "Zebra crossings are marked with white stripes."
        },
        {
          id: 8,
          question: "In residential areas, speed limit is usually:",
          options: [
            "60 km/h",
            "10 km/h",
            "25 km/h",
            "Drive at any speed"
          ],
          correctAnswer: 2,
          explanation: "Speed limit is 25 km/h in residential zones."
        },
        {
          id: 9,
          question: "When approaching an unguarded railway crossing, you should:",
          options: [
            "Accelerate quickly",
            "Look both ways and cross only if safe",
            "Sound horn and cross",
            "Stop in the middle"
          ],
          correctAnswer: 1,
          explanation: "Always ensure it's safe before crossing an unguarded railway."
        },
        {
          id: 10,
          question: "What is a safe way to enter a main road from a side road?",
          options: [
            "Reverse onto main road",
            "Check for traffic and proceed slowly",
            "Honk and enter quickly",
            "Wait for someone to wave you in"
          ],
          correctAnswer: 1,
          explanation: "Check for traffic carefully and proceed slowly."
        },
        {
          id: 11,
          question: "What color is used for information signs?",
          options: [
            "Blue",
            "Red",
            "Green",
            "White"
          ],
          correctAnswer: 0,
          explanation: "Information signs in India are blue."
        },
        {
          id: 12,
          question: "You should use headlights:",
          options: [
            "Only in the rain",
            "At all times",
            "Between sunset and sunrise or low visibility",
            "Never"
          ],
          correctAnswer: 2,
          explanation: "Use headlights at night and in low visibility."
        },
        {
          id: 13,
          question: "The 'No Entry' sign is usually:",
          options: [
            "Red circle with a white horizontal line",
            "Green square",
            "Blue rectangle",
            "Yellow triangle"
          ],
          correctAnswer: 0,
          explanation: "A red circle and white line indicate No Entry."
        },
        {
          id: 14,
          question: "Parking is NOT allowed:",
          options: [
            "Near intersections",
            "On flyovers",
            "Near bus stops",
            "All of the above"
          ],
          correctAnswer: 3,
          explanation: "Parking is restricted at all these locations."
        },
        {
          id: 15,
          question: "When two vehicles approach an intersection without traffic signals:",
          options: [
            "Vehicle on the right goes first",
            "Vehicle on the left goes first",
            "Whoever gets there first",
            "Bigger vehicle goes first"
          ],
          correctAnswer: 0,
          explanation: "Vehicle to the right has right of way."
        },
        {
          id: 16,
          question: "If you see a sign with 'U Turn Prohibited':",
          options: [
            "You can always U turn",
            "Can U turn with caution",
            "Cannot U turn at that place",
            "Overtaking allowed"
          ],
          correctAnswer: 2,
          explanation: "No U-turn means you should not take a U-turn."
        },
        {
          id: 17,
          question: "When should you dip your headlights?",
          options: [
            "During day",
            "When another vehicle approaches",
            "Never",
            "On highways"
          ],
          correctAnswer: 1,
          explanation: "Dip headlights to avoid blinding other drivers."
        },
        {
          id: 18,
          question: "How should loads be carried in a vehicle?",
          options: [
            "No restriction",
            "Can protrude sideways only",
            "Should not obstruct or project",
            "Can overhang backwards"
          ],
          correctAnswer: 2,
          explanation: "Loads must not obstruct traffic or project dangerously."
        },
        {
          id: 19,
          question: "Which document is NOT compulsory to keep in your vehicle?",
          options: [
            "Driving license",
            "Vehicle registration",
            "Mobile recharge receipt",
            "Insurance"
          ],
          correctAnswer: 2,
          explanation: "Mobile recharge receipt is not mandatory."
        },
        {
          id: 20,
          question: "A green arrow at traffic signal means:",
          options: [
            "Go in the direction shown",
            "Stop",
            "Wait",
            "Go in any direction"
          ],
          correctAnswer: 0,
          explanation: "Green arrow indicates proceed in the indicated direction only."
        }
      ]
    },
    // Easy 3
    {
      id: 3,
      title: "Road Safety",
      difficulty: "easy",
      icon: "🦺",
      totalQuestions: 20,
      questions: [
        {
          id: 1,
          question: "What should you do if you feel sleepy while driving?",
          options: ["Open windows", "Stop at a safe spot", "Have coffee only", "Keep driving slowly"],
          correctAnswer: 1,
          explanation: "Always stop in a safe place and rest if you're sleepy."
        },
        {
          id: 2,
          question: "For a safe journey, who should wear a seatbelt?",
          options: ["Front seat only", "Driver only", "Rear seat only", "All passengers"],
          correctAnswer: 3,
          explanation: "Seatbelts must be used by all, in front and in back."
        },
        {
          id: 3,
          question: "Hydroplaning occurs when:",
          options: [
            "Driving at low speed in dry weather",
            "Driving fast on wet surface",
            "Braking hard on dry road",
            "Driving slowly in heavy traffic"
          ],
          correctAnswer: 1,
          explanation: "Hydroplaning is loss of traction when driving fast on wet roads."
        },
        {
          id: 4,
          question: "Before overtaking, who should you check for?",
          options: ["Vehicles behind", "Oncoming traffic", "Traffic signals", "Both A and B"],
          correctAnswer: 3,
          explanation: "Always check behind and for oncoming vehicles before overtaking."
        },
        {
          id: 5,
          question: "At a pedestrian crossing, you:",
          options: [
            "Should speed up",
            "Must stop for pedestrians",
            "Honk and proceed",
            "Wait only if police are present"
          ],
          correctAnswer: 1,
          explanation: "Stop for pedestrians at crossings."
        },
        {
          id: 6,
          question: "Wearing light colored clothes at night:",
          options: [
            "Is a fashion",
            "Helps pedestrians be seen",
            "Distracts drivers",
            "Not advisable"
          ],
          correctAnswer: 1,
          explanation: "Helps drivers notice pedestrians at night."
        },
        {
          id: 7,
          question: "Animals on the road, you should:",
          options: [
            "Honk and pass",
            "Slow down and be ready to stop",
            "Ignore and continue",
            "Speed up"
          ],
          correctAnswer: 1,
          explanation: "Slow down and stop if necessary for animals."
        },
        {
          id: 8,
          question: "At a railroad crossing with gates open, you should:",
          options: [
            "Just cross immediately",
            "Check left and right before crossing",
            "Honk and cross",
            "Overtake waiting vehicles"
          ],
          correctAnswer: 1,
          explanation: "Always look both ways before crossing even if the gate is open."
        },
        {
          id: 9,
          question: "Foggy weather driving needs:",
          options: [
            "High beams",
            "Low beams/headlights/fog lamps",
            "No lights",
            "Hazard lights only"
          ],
          correctAnswer: 1,
          explanation: "Low beam or fog lamps are recommended in fog."
        },
        {
          id: 10,
          question: "Why is mobile phone use while driving dangerous?",
          options: [
            "It distracts attention",
            "No effect",
            "Helps focus",
            "Allows faster driving"
          ],
          correctAnswer: 0,
          explanation: "Mobile use diverts attention and leads to accidents."
        },
        {
          id: 11,
          question: "The safest way to store valuables in a car:",
          options: [
            "In locked glove compartment",
            "On dashboard",
            "On back seat",
            "Hanging from mirror"
          ],
          correctAnswer: 0,
          explanation: "Lock valuables out of sight in glove compartment."
        },
        {
          id: 12,
          question: "Skidding can be prevented by:",
          options: [
            "Braking hard suddenly",
            "Accelerating quickly",
            "Gentle use of brakes and accelerator",
            "Heavy load"
          ],
          correctAnswer: 2,
          explanation: "Use gentle, gradual movements to prevent skidding."
        },
        {
          id: 13,
          question: "Children under 12 should:",
          options: [
            "Ride in front seat",
            "Ride at the back with safety belt",
            "Stand in car",
            "Sit on lap"
          ],
          correctAnswer: 1,
          explanation: "Children should always be in the back seat with seatbelt."
        },
        {
          id: 14,
          question: "Using indicators while turning:",
          options: [
            "Is not mandatory",
            "Essential to inform others",
            "Only on highways",
            "Depends on mood"
          ],
          correctAnswer: 1,
          explanation: "Always indicate to let others know your intention."
        },
        {
          id: 15,
          question: "If brakes fail, what should you do first?",
          options: [
            "Accelerate more",
            "Downshift to lower gear and use hand brake",
            "Jump from car",
            "Sound horn only"
          ],
          correctAnswer: 1,
          explanation: "Downshift and gradually use the handbrake."
        },
        {
          id: 16,
          question: "When do most accidents occur?",
          options: [
            "On clear days",
            "At night in poor visibility",
            "As it rains after long dry spell",
            "During heavy police presence"
          ],
          correctAnswer: 2,
          explanation: "Roads are most slippery when it first starts raining after a dry period."
        },
        {
          id: 17,
          question: "A yellow diamond-shaped sign usually means:",
          options: [
            "Mandatory instruction",
            "Caution/Warning ahead",
            "Informative",
            "Playground"
          ],
          correctAnswer: 1,
          explanation: "Diamond yellow signs indicate caution."
        },
        {
          id: 18,
          question: "Blind spots are:",
          options: [
            "Cleaned by windshield wipers",
            "Areas not visible in mirrors",
            "Never exist in cars",
            "Dangerous only for trucks"
          ],
          correctAnswer: 1,
          explanation: "Blind spots are areas you can’t see in mirrors—check by turning your head."
        },
        {
          id: 19,
          question: "Using worn-out tyres:",
          options: [
            "Improves speed",
            "Reduces accident risk",
            "Is dangerous and reduces road grip",
            "Has no impact"
          ],
          correctAnswer: 2,
          explanation: "Always keep tyres in good condition for safety."
        },
        {
          id: 20,
          question: "To maintain maximum control, hands on the steering should be:",
          options: [
            "At 12 o'clock",
            "At 10 and 2 o’clock or 9 and 3 o’clock",
            "On your lap",
            "More on one side"
          ],
          correctAnswer: 1,
          explanation: "Use both hands at 9 and 3 or 10 and 2 o’clock position."
        }
      ]
    },
    // Easy 4
    {
      id: 4,
      title: "Sign Recognition",
      difficulty: "easy",
      icon: "🔰",
      totalQuestions: 20,
      questions: [
        {
          id: 1,
          question: "A blue square sign usually gives:",
          options: [
            "Information",
            "Caution",
            "Prohibition",
            "None"
          ],
          correctAnswer: 0,
          explanation: "Blue squares are information signs."
        },
        {
          id: 2,
          question: "What does a sign with a red circle and a white horizontal bar mean?",
          options: [
            "No Entry",
            "Danger ahead",
            "Parking allowed",
            "Left turn only"
          ],
          correctAnswer: 0,
          explanation: "A red circle with white bar means no entry."
        },
        {
          id: 3,
          question: "What does a sign with 30 inside a red circle mean?",
          options: [
            "30 km/h minimum",
            "30 km/h speed limit",
            "30 vehicles only",
            "Sharp curve after 30 meters"
          ],
          correctAnswer: 1,
          explanation: "Number inside red circle is speed limit."
        },
        {
          id: 4,
          question: "A black cross on a yellow triangle means:",
          options: [
            "Hospital ahead",
            "Cross road ahead",
            "T-junction",
            "Railway crossing"
          ],
          correctAnswer: 1,
          explanation: "Cross black mark in yellow triangle warns about crossroads."
        },
        {
          id: 5,
          question: "Sign with ‘H’ on blue rectangle means:",
          options: [
            "Highway",
            "Hospital ahead",
            "Halt",
            "Helipad"
          ],
          correctAnswer: 1,
          explanation: "‘H’ on blue rectangle indicates hospital ahead."
        },
        {
          id: 6,
          question: "A ‘No Horn’ sign is:",
          options: [
            "Mandatory",
            "Cautionary",
            "Informative",
            "None"
          ],
          correctAnswer: 0,
          explanation: "No Horn is a mandatory sign."
        },
        {
          id: 7,
          question: "A sign with an arrow bent left means:",
          options: [
            "No parking",
            "Left turn ahead",
            "Overtake from left",
            "Go straight"
          ],
          correctAnswer: 1,
          explanation: "Bent arrow symbolizes turn ahead."
        },
        {
          id: 8,
          question: "A sign with a picture of a school going child denotes:",
          options: [
            "Playground ahead",
            "School zone ahead",
            "No entry for children",
            "Zoo ahead"
          ],
          correctAnswer: 1,
          explanation: "School ahead sign warns of children crossing."
        },
        {
          id: 9,
          question: "A thick vertical bar with two thin lines on either side in a triangle means:",
          options: [
            "Narrow bridge ahead",
            "Widened road",
            "No overtaking",
            "Railway crossing"
          ],
          correctAnswer: 0,
          explanation: "Means narrow bridge or road ahead, proceed cautiously."
        },
        {
          id: 10,
          question: "A round sign with right-pointing arrow and cross over it means:",
          options: [
            "No right turn",
            "Right turn must",
            "Parking right",
            "Speed up"
          ],
          correctAnswer: 0,
          explanation: "Crossed arrow means no turn in that direction."
        },
        {
          id: 11,
          question: "A sign with a bump image in triangle warns of:",
          options: [
            "Falling rocks",
            "Speed breaker",
            "Low bridge",
            "Tunnel ahead"
          ],
          correctAnswer: 1,
          explanation: "Bump image in a triangle means speed breaker."
        },
        {
          id: 12,
          question: "Sign showing ‘P’ in red circle crossed out means:",
          options: [
            "Parking available",
            "Parking allowed",
            "No Parking",
            "Priority parking"
          ],
          correctAnswer: 2,
          explanation: "'P' crossed means No Parking."
        },
        {
          id: 13,
          question: "Blue rectangular sign with a white bus symbol indicates:",
          options: [
            "Bus lane",
            "Bus stand/stop",
            "No buses allowed",
            "Bus parking"
          ],
          correctAnswer: 1,
          explanation: "Indicates a bus stop."
        },
        {
          id: 14,
          question: "A round sign with a red circle and a truck image indicates:",
          options: [
            "Heavy vehicles allowed",
            "Trucks must go slowly",
            "No entry for heavy vehicles",
            "Parking for trucks"
          ],
          correctAnswer: 2,
          explanation: "Means no entry for heavy vehicles."
        },
        {
          id: 15,
          question: "Circular sign with bicycle image crossed out means:",
          options: [
            "Cycle stand",
            "No entry for cycles",
            "Cyclists only",
            "Cycle repair"
          ],
          correctAnswer: 1,
          explanation: "Cycle crossed out means cycles prohibited."
        },
        {
          id: 16,
          question: "A sign with figures of car and bike crossing means:",
          options: [
            "Free parking",
            "Both allowed",
            "Either may cross",
            "Uncontrolled crossing"
          ],
          correctAnswer: 3,
          explanation: "Means crossing without signals—exercise caution."
        },
        {
          id: 17,
          question: "Red bordered rectangle with black dot pattern means:",
          options: [
            "Falling rocks",
            "Slippery road",
            "Bumpy road",
            "Blocked road"
          ],
          correctAnswer: 1,
          explanation: "Black dots often indicate slippery road."
        },
        {
          id: 18,
          question: "White arrows in circle going in a loop mean:",
          options: [
            "Start",
            "No entry",
            "Roundabout ahead",
            "Right turn"
          ],
          correctAnswer: 2,
          explanation: "Circular arrows indicate roundabout."
        },
        {
          id: 19,
          question: "A sign with a car and wavy lines behind denotes:",
          options: [
            "Car wash nearby",
            "Slippery road ahead",
            "Speed up",
            "Well-paved road"
          ],
          correctAnswer: 1,
          explanation: "Car with wavy lines is a warning for slippery road."
        },
        {
          id: 20,
          question: "A sign with a truck on an incline means:",
          options: [
            "Hill station",
            "Steep descent ahead",
            "Truck stop",
            "Overtaking allowed"
          ],
          correctAnswer: 1,
          explanation: "Truck on slope marks a steep descent."
        }
      ]
    },
    // Easy 5
    {
      id: 5,
      title: "Beginner's Test",
      difficulty: "easy",
      icon: "📝",
      totalQuestions: 20,
      questions: [
        {
          id: 1,
          question: "What should you do when you see the traffic light turn yellow?",
          options: [
            "Speed up to cross before red",
            "Prepare to stop",
            "Stop only for the police",
            "Nothing"
          ],
          correctAnswer: 1,
          explanation: "Yellow means prepare to stop."
        },
        {
          id: 2,
          question: "Reverse gear should be used:",
          options: [
            "At pedestrian crossing",
            "To back up for parking or correction",
            "Anywhere for fun",
            "On main road"
          ],
          correctAnswer: 1,
          explanation: "Reverse only to back up or correct parking, never on main roads."
        },
        {
          id: 3,
          question: "What should you do before starting your vehicle?",
          options: [
            "Nothing required",
            "Check mirrors, seat belt, surroundings",
            "Switch on radio",
            "Accelerate immediately"
          ],
          correctAnswer: 1,
          explanation: "Always check mirrors, fasten seat belt."
        },
        {
          id: 4,
          question: "How would you signal for a turn if indicators fail?",
          options: [
            "Use hand signals",
            "No need to signal",
            "Shout loudly",
            "Switch lights off"
          ],
          correctAnswer: 0,
          explanation: "Use prescribed hand signals if indicators not working."
        },
        {
          id: 5,
          question: "If you must park at night, ensure:",
          options: [
            "No need for precautions",
            "Park in dark lane",
            "Switch on parking lights",
            "Park at intersection"
          ],
          correctAnswer: 2,
          explanation: "Always use parking lights at night for visibility."
        },
        {
          id: 6,
          question: "Should you use major horn in silent zone?",
          options: [
            "Yes, always",
            "No, prohibited",
            "When you feel like",
            "Only during rain"
          ],
          correctAnswer: 1,
          explanation: "Horns are prohibited in silent zones like near hospitals, schools."
        },
        {
          id: 7,
          question: "Broken white lines on the road mean:",
          options: [
            "You can change lanes or overtake if safe",
            "Do not overtake",
            "No entry",
            "Motorcycles only"
          ],
          correctAnswer: 0,
          explanation: "Broken white allows lane change if safe."
        },
        {
          id: 8,
          question: "If tyre bursts suddenly, you should:",
          options: [
            "Brake sharply",
            "Accelerate",
            "Hold steering, slow down gradually",
            "Turn immediately"
          ],
          correctAnswer: 2,
          explanation: "Slow gradually and stay in control if tyre bursts."
        },
        {
          id: 9,
          question: "Which one is not a valid driving license?",
          options: [
            "Expired license",
            "Valid Indian smart card license",
            "Digitally signed license",
            "Online print license from RTO"
          ],
          correctAnswer: 0,
          explanation: "Expired license is not valid."
        },
        {
          id: 10,
          question: "How should you merge onto a highway?",
          options: [
            "Suddenly",
            "Increase speed, check traffic, merge when clear",
            "Honk and enter",
            "Reverse"
          ],
          correctAnswer: 1,
          explanation: "Match speed and merge smoothly when clear."
        },
        {
          id: 11,
          question: "The color of the number plate on a private car is:",
          options: [
            "White letters on black",
            "Black letters on white",
            "Yellow background",
            "Red"
          ],
          correctAnswer: 1,
          explanation: "Private cars have black on white plate."
        },
        {
          id: 12,
          question: "What is the sign for railway crossing without gate?",
          options: [
            "Circle with two tracks",
            "Yellow triangle with railway lines",
            "Red X",
            "Blue square"
          ],
          correctAnswer: 1,
          explanation: "Triangle with lines denotes ungated railway crossing."
        },
        {
          id: 13,
          question: "What is the use of a silencer in motor vehicles?",
          options: [
            "For radio",
            "To reduce engine noise",
            "Increase speed",
            "Decrease temperature"
          ],
          correctAnswer: 1,
          explanation: "Silencer minimizes exhaust and engine sound."
        },
        {
          id: 14,
          question: "A motoring zone is indicated by:",
          options: [
            "Blue information board",
            "Yellow circle",
            "Green rectangle",
            "Red triangle"
          ],
          correctAnswer: 0,
          explanation: "Motoring zones use blue info boards."
        },
        {
          id: 15,
          question: "While crossing a one-way street:",
          options: [
            "Look only one way",
            "Look both ways",
            "No need to look",
            "Run quickly"
          ],
          correctAnswer: 1,
          explanation: "Always look both ways before crossing any street."
        },
        {
          id: 16,
          question: "The road surface marked with STOP means:",
          options: [
            "No need to stop",
            "Stop completely and proceed only after checking",
            "Speed up",
            "Look at driver behind"
          ],
          correctAnswer: 1,
          explanation: "Stop and ensure it is safe before proceeding."
        },
        {
          id: 17,
          question: "Roundabout rules require:",
          options: [
            "Give way to traffic from right",
            "Always accelerate inside",
            "Use leftmost lane for all turns",
            "Overtake inside roundabout"
          ],
          correctAnswer: 0,
          explanation: "Yield to the right on entering roundabout."
        },
        {
          id: 18,
          question: "To drive in fog you should:",
          options: [
            "Keep lights off",
            "Drive fast",
            "Use fog lamps and proceed slowly",
            "Blow horn repeatedly"
          ],
          correctAnswer: 2,
          explanation: "Use fog lamps, drive slow and carefully in fog."
        },
        {
          id: 19,
          question: "In which year did the current Motor Vehicles Act come into force?",
          options: [
            "1988",
            "2005",
            "2019",
            "1965"
          ],
          correctAnswer: 0,
          explanation: "The Motor Vehicles Act, 1988 governs current rules."
        },
        {
          id: 20,
          question: "When finding an injured person at accident site, you should:",
          options: [
            "Leave immediately",
            "Help and call emergency services",
            "Take photos only",
            "Shout at others"
          ],
          correctAnswer: 1,
          explanation: "Always help and inform emergency services promptly."
        }
      ]
    },
    // Intermediate 1
    {
      id: 6,
      title: "Penalties & Fines",
      difficulty: "intermediate",
      icon: "💸",
      totalQuestions: 20,
      questions: [
        {
          id: 1,
          question: "What is the penalty for not wearing a seat belt as per the Motor Vehicles Act (2019)?",
          options: ["₹100", "₹500", "₹1000", "₹2000"],
          correctAnswer: 1,
          explanation: "Fine is ₹500 for not wearing a seat belt."
        },
        {
          id: 2,
          question: "Penalty for drunk driving can include:",
          options: [
            "Fine only",
            "Fine and/or imprisonment",
            "License suspension",
            "Both B and C"
          ],
          correctAnswer: 3,
          explanation: "You can face both fine, license suspension, and even imprisonment."
        },
        {
          id: 3,
          question: "Fine for using mobile phone while driving is:",
          options: [
            "₹100",
            "₹2,000 for first offence",
            "₹500",
            "₹10,000"
          ],
          correctAnswer: 1,
          explanation: "Fine for phone use is ₹2,000 for first time, ₹10,000 for repeat."
        },
        {
          id: 4,
          question: "Driving without a valid license leads to:",
          options: ["Warning", "Fine up to ₹5000", "No action", "License renewal"],
          correctAnswer: 1,
          explanation: "Fine may go up to ₹5,000 under new rules."
        },
        {
          id: 5,
          question: "Penalty for over-speeding LMV (car) is:",
          options: [
            "₹400",
            "₹1,000 - ₹2,000",
            "₹5,000",
            "₹1,000 only"
          ],
          correctAnswer: 1,
          explanation: "Fine for over-speeding LMV is ₹1,000 - ₹2,000."
        },
        {
          id: 6,
          question: "Blocking emergency vehicles may result in:",
          options: [
            "Verbal warning only",
            "Fine up to ₹10,000",
            "License cancellation",
            "None"
          ],
          correctAnswer: 1,
          explanation: "Serious action and up to ₹10,000 fine."
        },
        {
          id: 7,
          question: "Which section deals with dangerous driving penalty?",
          options: [
            "MV Act Sec 177",
            "Sec 184",
            "Sec 185",
            "Sec 202"
          ],
          correctAnswer: 1,
          explanation: "Section 184 deals with dangerous driving."
        },
        {
          id: 8,
          question: "No insurance present while driving attracts penalty of:",
          options: [
            "₹500",
            "₹2,000/3 months jail or both",
            "₹100",
            "₹5,000"
          ],
          correctAnswer: 1,
          explanation: "Fine is ₹2,000 or up to 3 months jail."
        },
        {
          id: 9,
          question: "Fine for driving unregistered vehicle is:",
          options: [
            "₹500",
            "₹2,000",
            "₹5000",
            "₹10,000"
          ],
          correctAnswer: 3,
          explanation: "It is ₹10,000 for driving unregistered vehicle."
        },
        {
          id: 10,
          question: "What if a minor (under 18) is found driving?",
          options: [
            "Car gets towed only",
            "Guardian/owner is deemed guilty",
            "Nothing happens",
            "Verbal warning"
          ],
          correctAnswer: 1,
          explanation: "Guardian/vehicle owner is held responsible as per law."
        },
        {
          id: 11,
          question: "What is penalty for violating traffic signals repeatedly?",
          options: [
            "Warning only",
            "Fine & possible license disqualification",
            "Community service",
            "Vehicle confiscation"
          ],
          correctAnswer: 1,
          explanation: "Multiple offences may lead to disqualification."
        },
        {
          id: 12,
          question: "Riding a two-wheeler without helmet penalty (2019):",
          options: [
            "₹100",
            "₹500",
            "₹1,000",
            "₹200"
          ],
          correctAnswer: 1,
          explanation: "Penalty is ₹500 per the latest rule."
        },
        {
          id: 13,
          question: "Commercial vehicle violating pollution norms penalty:",
          options: [
            "Up to ₹10,000",
            "₹1,000",
            "Up to ₹50,000",
            "₹250"
          ],
          correctAnswer: 0,
          explanation: "Pollution violation fine can go up to ₹10,000."
        },
        {
          id: 14,
          question: "Fine for not giving way to ambulance or fire truck:",
          options: [
            "₹2,000",
            "₹10,000",
            "₹100",
            "No fine"
          ],
          correctAnswer: 1,
          explanation: "Fine can be up to ₹10,000."
        },
        {
          id: 15,
          question: "Repeated dangerous driving can result in:",
          options: [
            "License suspension",
            "Imprisonment",
            "Higher fines",
            "All of these"
          ],
          correctAnswer: 3,
          explanation: "Repeated offences invite license suspension, higher fines and/or jail."
        },
        {
          id: 16,
          question: "Penalty for racing or speeding in public places:",
          options: [
            "₹5,000 for first offence",
            "₹200 only",
            "Community Service",
            "No penalty"
          ],
          correctAnswer: 0,
          explanation: "Fine for racing/speeding starts at ₹5,000."
        },
        {
          id: 17,
          question: "Penalty for unauthorized use of vehicle without permit:",
          options: [
            "₹2,000",
            "₹5,000",
            "₹10,000",
            "₹500"
          ],
          correctAnswer: 2,
          explanation: "Unauthorized use attracts ₹10,000 fine."
        },
        {
          id: 18,
          question: "Fine for not producing vehicle documents on demand:",
          options: [
            "₹100",
            "₹500",
            "₹200",
            "₹1,000"
          ],
          correctAnswer: 3,
          explanation: "Fine is ₹1,000 for missing or unavailable documents."
        },
        {
          id: 19,
          question: "Penalty for overloading passengers in a private car:",
          options: [
            "₹500 per additional person",
            "₹2,000 flat",
            "No penalty",
            "₹50"
          ],
          correctAnswer: 0,
          explanation: "Fine is ₹500 per extra passenger."
        },
        {
          id: 20,
          question: "Fine for talking on mobile phone while driving a two-wheeler:",
          options: [
            "₹1,000",
            "₹2,000",
            "₹10,000",
            "₹100"
          ],
          correctAnswer: 1,
          explanation: "Fine is ₹2,000."
        }
      ]
    },
    // Intermediate 2
    {
      id: 7,
      title: "Right of Way",
      difficulty: "intermediate",
      icon: "➡️",
      totalQuestions: 20,
      questions: [
        {
          id: 1,
          question: "Who gets right of way at a T-junction?",
          options: [
            "Vehicle on main road",
            "Vehicle turning right",
            "Vehicle on side road",
            "Pedestrians"
          ],
          correctAnswer: 0,
          explanation: "Main road traffic has right of way."
        },
        {
          id: 2,
          question: "In roundabouts, who should be given priority?",
          options: [
            "Vehicles entering",
            "Vehicles waiting",
            "Vehicles already in roundabout",
            "None"
          ],
          correctAnswer: 2,
          explanation: "Always give priority to vehicles inside the roundabout."
        },
        {
          id: 3,
          question: "At pedestrian crossings, who gets priority?",
          options: [
            "Cars",
            "Pedestrians",
            "Cyclists",
            "All equally"
          ],
          correctAnswer: 1,
          explanation: "Pedestrians have right of way."
        },
        {
          id: 4,
          question: "When entering a highway from slip road, you must:",
          options: [
            "Give way to highway traffic",
            "Expect others to stop",
            "Accelerate to merge regardless",
            "Stop in the main lane"
          ],
          correctAnswer: 0,
          explanation: "Highway traffic gets right of way."
        },
        {
          id: 5,
          question: "On mountain roads, who has priority at an incline?",
          options: [
            "Vehicle going downhill",
            "Vehicle coming uphill",
            "All vehicles",
            "No one"
          ],
          correctAnswer: 1,
          explanation: "Uphill vehicle has right of way."
        },
        {
          id: 6,
          question: "Yield sign instructs drivers to:",
          options: [
            "Speed up",
            "Give the right of way to other vehicles",
            "Block other vehicles",
            "None"
          ],
          correctAnswer: 1,
          explanation: "Yield means let others go first."
        },
        {
          id: 7,
          question: "When exiting a parking lot onto a road:",
          options: [
            "You have right of way",
            "Give way to through traffic",
            "Park in the road",
            "Wait for horn"
          ],
          correctAnswer: 1,
          explanation: "Through traffic always gets priority."
        },
        {
          id: 8,
          question: "Emergency vehicles using sirens must be:",
          options: [
            "Ignored",
            "Given right of way immediately",
            "Challenged",
            "Followed"
          ],
          correctAnswer: 1,
          explanation: "Always give way to emergency vehicles."
        },
        {
          id: 9,
          question: "At railway crossings without gates:",
          options: [
            "Drive without stopping",
            "Train has right of way",
            "You have right of way",
            "None"
          ],
          correctAnswer: 1,
          explanation: "Trains always have the right of way."
        },
        {
          id: 10,
          question: "When two vehicles reach a junction at the same time:",
          options: [
            "Leftmost vehicle goes first",
            "Bigger vehicle goes first",
            "Vehicle on the right goes first",
            "Whoever signals first"
          ],
          correctAnswer: 2,
          explanation: "In India, vehicle on the right gets priority."
        },
        {
          id: 11,
          question: "Right of way on a narrow bridge goes to:",
          options: [
            "Vehicle closest to the bridge",
            "Heavy vehicles only",
            "Buses",
            "Fastest vehicle"
          ],
          correctAnswer: 0,
          explanation: "First vehicle to approach gets right of way."
        },
        {
          id: 12,
          question: "At uncontrolled intersection, who goes first?",
          options: [
            "Vehicle to the right",
            "Vehicle to the left",
            "Smaller vehicle",
            "Pedestrian always"
          ],
          correctAnswer: 0,
          explanation: "Vehicle on the right gets priority."
        },
        {
          id: 13,
          question: "If two vehicles enter a roundabout at the same time, who goes?",
          options: [
            "Vehicle on right",
            "Vehicle from left",
            "Bigger vehicle",
            "Whoever is faster"
          ],
          correctAnswer: 0,
          explanation: "Vehicle to the right has precedence."
        },
        {
          id: 14,
          question: "On a one-way street merging traffic must:",
          options: [
            "Always merge first",
            "Give way to vehicles already on street",
            "Stop in the middle",
            "Honk and enter"
          ],
          correctAnswer: 1,
          explanation: "Give way to vehicles already on the main road."
        },
        {
          id: 15,
          question: "While overtaking, the vehicle being overtaken should:",
          options: [
            "Increase speed",
            "Maintain steady speed",
            "Stop immediately",
            "Honk to warn"
          ],
          correctAnswer: 1,
          explanation: "Maintain steady speed for safe overtaking."
        },
        {
          id: 16,
          question: "On slip roads, merging vehicles:",
          options: [
            "Have right of way",
            "Must wait for gap in traffic",
            "Block others",
            "Stop in moving lane"
          ],
          correctAnswer: 1,
          explanation: "Wait for safe gap while merging."
        },
        {
          id: 17,
          question: "Who must give way if you are turning left and pedestrian is crossing?",
          options: [
            "Pedestrian waits",
            "Driver gives way",
            "Both cross together",
            "Neither"
          ],
          correctAnswer: 1,
          explanation: "Driver must always give way to pedestrian."
        },
        {
          id: 18,
          question: "At a major intersection, who has right of way if turning right?",
          options: [
            "Oncoming straight traffic",
            "Right turning vehicle",
            "Cyclist",
            "None"
          ],
          correctAnswer: 0,
          explanation: "Oncoming straight-moving traffic has the right."
        },
        {
          id: 19,
          question: "When traffic lights are not working:",
          options: [
            "Ignore other vehicles",
            "Follow first come, first go rule",
            "Follow hand signals or priority rules",
            "Honk and proceed"
          ],
          correctAnswer: 2,
          explanation: "Follow hand signals or right of way priorities."
        },
        {
          id: 20,
          question: "In parking lots, car moving out should:",
          options: [
            "Go first",
            "Give way to entering cars",
            "Sound horn and move",
            "Block entry"
          ],
          correctAnswer: 1,
          explanation: "Exiting cars should give way to entering vehicles."
        }
      ]
    },
    // Intermediate 3
    {
      id: 8,
      title: "Safe Driving Practices",
      difficulty: "intermediate",
      icon: "🛡️",
      totalQuestions: 20,
      questions: [
        {
          id: 1,
          question: "When driving in rain, you should:",
          options: [
            "Use high beam only",
            "Slow down and increase following distance",
            "Turn lights off",
            "Speed up"
          ],
          correctAnswer: 1,
          explanation: "Increase gap and slow down in rain."
        },
        {
          id: 2,
          question: "The safest way to use a mobile phone in a car is:",
          options: [
            "While at signals",
            "With hands-free system only when parked",
            "When driving slowly",
            "At high speeds"
          ],
          correctAnswer: 1,
          explanation: "Use phone only when stationary and parked."
        },
        {
          id: 3,
          question: "Checking tire pressure:",
          options: [
            "Is optional",
            "Should be regular",
            "Only for puncture",
            "After long drive only"
          ],
          correctAnswer: 1,
          explanation: "Tire pressure should be checked regularly for safety."
        },
        {
          id: 4,
          question: "The 'Two second rule' is used for:",
          options: [
            "Fuel saving",
            "Safe distance from car ahead",
            "Speed calculation",
            "Horn usage"
          ],
          correctAnswer: 1,
          explanation: "Maintain 2 seconds behind vehicle ahead."
        },
        {
          id: 5,
          question: "You should adjust your mirrors:",
          options: [
            "Only at night",
            "Before starting journey",
            "After parking",
            "Not required"
          ],
          correctAnswer: 1,
          explanation: "Always set mirrors before driving."
        },
        {
          id: 6,
          question: "When overtaking, you should:",
          options: [
            "Check mirrors and blind spot",
            "Honk and speed up",
            "Overtake from left",
            "Follow closely"
          ],
          correctAnswer: 0,
          explanation: "Check all sides before overtaking."
        },
        {
          id: 7,
          question: "When driving behind a large vehicle:",
          options: [
            "Follow very closely",
            "Stay out of its blind spots and maintain distance",
            "Honk repeatedly",
            "None"
          ],
          correctAnswer: 1,
          explanation: "Blind spots of large vehicles are bigger, so stay clear."
        },
        {
          id: 8,
          question: "If you are tired during a long drive:",
          options: [
            "Keep going",
            "Take a break and rest",
            "Drive faster to reach soon",
            "Open window only"
          ],
          correctAnswer: 1,
          explanation: "Rest when tired; do not drive drowsy."
        },
        {
          id: 9,
          question: "Low visibility is best handled by:",
          options: [
            "Parking in middle lane",
            "Using fog lamps or low beam",
            "Using high beam",
            "Switch lights off"
          ],
          correctAnswer: 1,
          explanation: "Use low-beam headlights or fog lamps."
        },
        {
          id: 10,
          question: "What should you do if a pedestrian appears suddenly?",
          options: [
            "Honk only",
            "Slow down or stop",
            "Accelerate",
            "Ignore"
          ],
          correctAnswer: 1,
          explanation: "Always be prepared to stop for sudden pedestrians."
        },
        {
          id: 11,
          question: "When parking on an incline uphill:",
          options: [
            "Face wheels away from curb and use handbrake",
            "Don’t use handbrake",
            "Leave engine running",
            "Face wheels toward curb"
          ],
          correctAnswer: 0,
          explanation: "Wheel away from curb and use handbrake to avoid rolling back."
        },
        {
          id: 12,
          question: "Horn must NOT be used:",
          options: [
            "Near hospitals and schools",
            "On highways",
            "On bridges",
            "On crossings"
          ],
          correctAnswer: 0,
          explanation: "Avoid horn use near sensitive areas."
        },
        {
          id: 13,
          question: "Wet leaves on the road:",
          options: [
            "Make surface more grippy",
            "Can be as slippery as ice",
            "No effect",
            "Help braking"
          ],
          correctAnswer: 1,
          explanation: "Wet leaves can be slippery as ice."
        },
        {
          id: 14,
          question: "If you see a ball roll under your car from sidewalk:",
          options: [
            "Ignore",
            "Expect a child to follow",
            "Accelerate",
            "Reverse"
          ],
          correctAnswer: 1,
          explanation: "Expect a child if a ball comes onto road."
        },
        {
          id: 15,
          question: "Passing stationary bus on a school zone:",
          options: [
            "Accelerate",
            "Slow down and look out for crossing children",
            "Ignore",
            "Honk"
          ],
          correctAnswer: 1,
          explanation: "Beware of children crossing near stationary bus."
        },
        {
          id: 16,
          question: "One reason for low tire pressure is:",
          options: [
            "Better grip",
            "Heavier steering",
            "Decreased efficiency and safety",
            "No effect"
          ],
          correctAnswer: 2,
          explanation: "Low pressure increases rolling resistance and is unsafe."
        },
        {
          id: 17,
          question: "Whenever you change lanes:",
          options: [
            "Just move over",
            "Indicate and check mirrors and blind spot",
            "Speed up",
            "Nobody needs to know"
          ],
          correctAnswer: 1,
          explanation: "Always signal and check blind spots."
        },
        {
          id: 18,
          question: "Speed in residential area should:",
          options: [
            "Never exceed 25 km/h",
            "Be fast",
            "Never matter",
            "Match highway"
          ],
          correctAnswer: 0,
          explanation: "Keep speed less than 25 km/h."
        },
        {
          id: 19,
          question: "Which one is NOT recommended during long journey?",
          options: [
            "Take regular breaks",
            "Drive sleepy",
            "Eat lightly beforehand",
            "Plan the route"
          ],
          correctAnswer: 1,
          explanation: "Never drive when tired or sleepy."
        },
        {
          id: 20,
          question: "A blown out fuse can cause:",
          options: [
            "Brake failure",
            "No effect",
            "Electrical failure (lights/horn/indicators)",
            "Engine seizure"
          ],
          correctAnswer: 2,
          explanation: "Fuses protect and can cause failure in electric systems if blown."
        }
      ]
    },
    // Hard 1
    {
      id: 9,
      title: "Advanced Drivers",
      difficulty: "hard",
      icon: "🏆",
      totalQuestions: 20,
      questions: [
        {
          id: 1,
          question: "According to Indian law, what is the maximum permissible BAC (Blood Alcohol Content) level for private car drivers?",
          options: [
            "0.03%",
            "0.05%",
            "0.08%",
            "Zero tolerance"
          ],
          correctAnswer: 0,
          explanation: "The legal limit is 0.03% BAC for private vehicles."
        },
        {
          id: 2,
          question: "Section 185 of the Motor Vehicles Act deals with:",
          options: [
            "Speeding",
            "Drunken driving",
            "Registration of vehicles",
            "Helmet rule"
          ],
          correctAnswer: 1,
          explanation: "Sec 185 relates to punishment for driving under the influence."
        },
        {
          id: 3,
          question: "A vehicle's registration can be suspended for repeated offences for up to:",
          options: [
            "1 month",
            "3 months",
            "6 months",
            "1 year"
          ],
          correctAnswer: 2,
          explanation: "Vehicle registration can be suspended up to 6 months."
        },
        {
          id: 4,
          question: "The validity of a private vehicle registration certificate is:",
          options: [
            "10 years",
            "15 years",
            "20 years",
            "5 years"
          ],
          correctAnswer: 1,
          explanation: "Private vehicles are registered for 15 years initially."
        },
        {
          id: 5,
          question: "Narrow bridge sign requires driver to:",
          options: [
            "Stop always",
            "Proceed slowly and carefully",
            "Increase speed",
            "Reverse"
          ],
          correctAnswer: 1,
          explanation: "Reduce speed and give way if necessary."
        },
        {
          id: 6,
          question: "The meaning of a continuous yellow line in the center is:",
          options: [
            "Overtaking allowed",
            "No overtaking or crossing",
            "Parking area",
            "Stop only"
          ],
          correctAnswer: 1,
          explanation: "No overtaking or lane crossing."
        },
        {
          id: 7,
          question: "A PUCC is required for:",
          options: [
            "All vehicles",
            "Only two-wheelers",
            "Commercial vehicles only",
            "None"
          ],
          correctAnswer: 0,
          explanation: "Pollution Under Control Certificate is required for all vehicles."
        },
        {
          id: 8,
          question: "What is the main purpose of ABS (Anti-lock Braking System)?",
          options: [
            "Stability during braking",
            "Increase speed",
            "Better mileage",
            "Reduce emissions"
          ],
          correctAnswer: 0,
          explanation: "ABS prevents wheel lock and improves steering control."
        },
        {
          id: 9,
          question: "One-way street violation penalty is:",
          options: [
            "₹100",
            "₹500",
            "₹2,000",
            "₹10,000"
          ],
          correctAnswer: 1,
          explanation: "Fine is ₹500 for violating one-way rule."
        },
        {
          id: 10,
          question: "Maximum speed limit allowed in urban area (unless otherwise posted):",
          options: [
            "25 km/h",
            "50 km/h",
            "60 km/h",
            "80 km/h"
          ],
          correctAnswer: 1,
          explanation: "50 km/h is the general maximum in urban area."
        },
        {
          id: 11,
          question: "Who certifies the vehicle fitness for commercial vehicles?",
          options: [
            "RTO",
            "Dealer",
            "Insurance company",
            "Owner"
          ],
          correctAnswer: 0,
          explanation: "RTO issues vehicle fitness certificates."
        },
        {
          id: 12,
          question: "When does a new private vehicle registration become renewable?",
          options: [
            "After 10 years",
            "After 15 years",
            "After 5 years",
            "After 20 years"
          ],
          correctAnswer: 1,
          explanation: "RC is renewed after 15 years."
        },
        {
          id: 13,
          question: "Black smoke from diesel vehicles indicates:",
          options: [
            "Efficient engine",
            "Incomplete combustion/pollution",
            "No issue",
            "Running on petrol"
          ],
          correctAnswer: 1,
          explanation: "Black smoke indicates incomplete combustion."
        },
        {
          id: 14,
          question: "Stopping distance increases with:",
          options: [
            "Increased speed",
            "Worn tyres",
            "Wet roads",
            "All of the above"
          ],
          correctAnswer: 3,
          explanation: "All factors increase stopping distance."
        },
        {
          id: 15,
          question: "Hydrocarbon emissions are caused mainly by:",
          options: [
            "Combustion in engines",
            "Paint job",
            "Tyre friction",
            "Air conditioning"
          ],
          correctAnswer: 0,
          explanation: "Engine combustion is primary source."
        },
        {
          id: 16,
          question: "A 'blind spot' in heavy vehicles is:",
          options: [
            "Behind the vehicle only",
            "Area not visible in mirrors",
            "On the dashboard",
            "Next to steering wheel"
          ],
          correctAnswer: 1,
          explanation: "Blind spot = areas you can’t directly see from driver’s seat."
        },
        {
          id: 17,
          question: "The owner is liable for:",
          options: [
            "Only mechanical defects",
            "Legal compliance and insurance",
            "Nothing",
            "Trip planning"
          ],
          correctAnswer: 1,
          explanation: "Owner must ensure vehicle is legal and insured."
        },
        {
          id: 18,
          question: "A 'double solid line' in road center means:",
          options: [
            "Do not overtake/cross from either side",
            "Overtake only at night",
            "Cycle lane",
            "No effect"
          ],
          correctAnswer: 0,
          explanation: "No overtaking at all with double solid line."
        },
        {
          id: 19,
          question: "Airbags provide protection during:",
          options: [
            "Engine failure",
            "Accidents (frontal impact)",
            "Rain",
            "Reverse"
          ],
          correctAnswer: 1,
          explanation: "Airbags reduce risk in collisions."
        },
        {
          id: 20,
          question: "A vehicle can be impounded if:",
          options: [
            "License is invalid",
            "Insurance expired",
            "Pollution certificate not available",
            "All of the above"
          ],
          correctAnswer: 3,
          explanation: "Vehicle can be seized for all above violations."
        }
      ]
    },
    // Hard 2
    {
      id: 10,
      title: "Expert Challenge",
      difficulty: "hard",
      icon: "🚗💨",
      totalQuestions: 20,
      questions: [
        {
          id: 1,
          question: "What is the correct way to deal with aquaplaning?",
          options: [
            "Brake hard",
            "Ease off accelerator, steer in the direction you want to go",
            "Accelerate more",
            "Turn off ignition"
          ],
          correctAnswer: 1,
          explanation: "Ease off accelerator and steer gently during aquaplaning."
        },
        {
          id: 2,
          question: "Section 184 of MV Act penalizes:",
          options: [
            "Drunk driving",
            "Dangerous driving (including overspeeding)",
            "Uninsured driving",
            "Helmetless riding"
          ],
          correctAnswer: 1,
          explanation: "Section 184 applies to rash and negligent driving."
        },
        {
          id: 3,
          question: "For commercial vehicles, fitness certificate is renewed every:",
          options: [
            "5 years after 40 years old",
            "1 year",
            "6 months",
            "20 years"
          ],
          correctAnswer: 1,
          explanation: "Fitness certificate must be renewed every year for commercial vehicles."
        },
        {
          id: 4,
          question: "Validity of learning licence in India is:",
          options: [
            "3 months",
            "6 months",
            "1 year",
            "5 years"
          ],
          correctAnswer: 1,
          explanation: "Learner's licence is valid for 6 months."
        },
        {
          id: 5,
          question: "Which of these is the correct order for registering a new vehicle?",
          options: [
            "Obtain insurance → Vehicle registration → Pollution certificate",
            "Get insurance → Pollution certificate → Registration",
            "Pay tax → Driving licence",
            "None of above"
          ],
          correctAnswer: 0,
          explanation: "Get insurance first, then register, then PUCC."
        },
        {
          id: 6,
          question: "Which is NOT considered dangerous driving under Section 184?",
          options: [
            "Overspeeding",
            "Using cellphone while driving",
            "Honking unnecessarily",
            "Driving in wrong lane"
          ],
          correctAnswer: 2,
          explanation: "Honking is not directly dangerous driving."
        },
        {
          id: 7,
          question: "Speed limit for cars on expressway (if not indicated)?",
          options: [
            "60 km/h",
            "100 km/h",
            "120 km/h",
            "80 km/h"
          ],
          correctAnswer: 1,
          explanation: "Expressways generally permit up to 100 km/h."
        },
        {
          id: 8,
          question: "What is an 'amber' light at the traffic signal?",
          options: [
            "Go",
            "Warning/prepare to stop",
            "Speed up",
            "Turn only"
          ],
          correctAnswer: 1,
          explanation: "Amber (yellow) means warning/prepare to stop."
        },
        {
          id: 9,
          question: "Under what circumstances can horn be used?",
          options: [
            "Alerting other road users to avoid danger",
            "Silent zones",
            "At signals always",
            "For fun"
          ],
          correctAnswer: 0,
          explanation: "Use horn only to alert in dangerous situations."
        },
        {
          id: 10,
          question: "Effective stopping distance depends on:",
          options: [
            "Road condition and vehicle speed",
            "Color of the car",
            "Fuel level",
            "Type of seat cover"
          ],
          correctAnswer: 0,
          explanation: "Stopping distance is affected by road condition and speed."
        },
        {
          id: 11,
          question: "Headlights must be dimmed:",
          options: [
            "When overtaking",
            "When another vehicle approaches",
            "In tunnels",
            "Never"
          ],
          correctAnswer: 1,
          explanation: "Dim lights for approaching vehicles."
        },
        {
          id: 12,
          question: "What should be checked for safe operation of air bags?",
          options: [
            "Tyre pressure",
            "Seat belt use",
            "Engine oil",
            "Air conditioning"
          ],
          correctAnswer: 1,
          explanation: "Air bags work best when seat belts are used."
        },
        {
          id: 13,
          question: "Continuous honking in traffic may lead to:",
          options: [
            "Accidents",
            "Distraction",
            "Noise pollution",
            "All of these"
          ],
          correctAnswer: 3,
          explanation: "Creates nuisance, may cause accidents."
        },
        {
          id: 14,
          question: "On highways, which lane should be used for overtaking?",
          options: [
            "Left",
            "Rightmost lane",
            "Middle",
            "Any"
          ],
          correctAnswer: 1,
          explanation: "Overtake from rightmost lane only."
        },
        {
          id: 15,
          question: "You can drive a transport vehicle with a learner's license:",
          options: [
            "Yes",
            "No",
            "Yes with owner's permission",
            "In emergencies"
          ],
          correctAnswer: 1,
          explanation: "Learner’s license is not valid for transport vehicles."
        },
        {
          id: 16,
          question: "Main cause of skidding on wet road:",
          options: [
            "Deflated tires",
            "Sudden acceleration or braking",
            "Clean windscreen",
            "Heavy vehicle"
          ],
          correctAnswer: 1,
          explanation: "Sudden moves cause skidding."
        },
        {
          id: 17,
          question: "Travelling in goods compartment of truck is:",
          options: [
            "Allowed",
            "Not allowed",
            "Permitted at night",
            "Safe"
          ],
          correctAnswer: 1,
          explanation: "It is illegal and unsafe."
        },
        {
          id: 18,
          question: "If caught overspeeding multiple times:",
          options: [
            "No issue",
            "Higher fines and license suspension risk",
            "Sirens installed",
            "Praise from police"
          ],
          correctAnswer: 1,
          explanation: "Repeat offences increase penalty and risk of license loss."
        },
        {
          id: 19,
          question: "Misuse of fog lamps in clear weather may result in:",
          options: [
            "Better vision",
            "Glare and distraction to others",
            "Faster driving",
            "Lower penalty"
          ],
          correctAnswer: 1,
          explanation: "Can blind oncoming traffic."
        },
        {
          id: 20,
          question: "Maximum permissible width of a vehicle as per Indian rules is:",
          options: [
            "1.8 m",
            "2.6 m",
            "3.2 m",
            "3.8 m"
          ],
          correctAnswer: 1,
          explanation: "Maximum width is 2.6 meters."
        }
      ]
    }
  ],
  hindi: [
    {
      id: 1,
      title: "ट्रैफिक बेसिक्स",
      difficulty: "easy",
      icon: "🚦",
      totalQuestions: 20,
      questions: [
        {
          id: 1,
          question: "लाल ट्रैफिक लाइट का क्या मतलब होता है?",
          options: [
            "सावधानी से आगे बढ़ें",
            "पूरी तरह रुकें",
            "गति बढ़ाएं",
            "हॉर्न बजाएं"
          ],
          correctAnswer: 1,
          explanation: "लाल का मतलब है रुकें। सदैव लाल लाइट पर वाहन को पूरी तरह रोकें।"
        },
        {
          id: 2,
          question: "भारत में कार के लिये लर्नर लाइसेंस लेने की न्यूनतम आयु क्या है?",
          options: [
            "16 वर्ष",
            "18 वर्ष",
            "20 वर्ष",
            "21 वर्ष"
          ],
          correctAnswer: 1,
          explanation: "कार के लिए लर्नर लाइसेंस 18 वर्ष या उससे अधिक आयु के लिए होता है।"
        },
        {
          id: 3,
          question: "हरी ट्रैफिक लाइट का क्या अर्थ होता है?",
          options: [
            "रुकें",
            "जाएँ",
            "तैयार हो जाएं",
            "धीमे चलें"
          ],
          correctAnswer: 1,
          explanation: "हरी लाइट का मतलब है जब रास्ता खाली हो तो आप आगे बढ़ सकते हैं।"
        },
        {
          id: 4,
          question: "भारत में किस तरफ से ओवरटेकिंग करनी चाहिए?",
          options: [
            "दाईं तरफ",
            "बाईं तरफ",
            "किसी भी तरफ",
            "ओवरटेकिंग की अनुमति नहीं"
          ],
          correctAnswer: 0,
          explanation: "भारत में ओवरटेकिंग दाईं तरफ से की जाती है।"
        },
        {
          id: 5,
          question: "निम्नलिखित में से कौन सा रोड साइन अनिवार्य होता है?",
          options: [
            "स्पीड लिमिट",
            "वे दे",
            "अस्पताल आगे",
            "नो पार्किंग"
          ],
          correctAnswer: 0,
          explanation: "स्पीड लिमिट साइन अनिवार्य होते हैं।"
        },
        {
          id: 6,
          question: "राउंडअबाउट पर किसका अधिकार सर्वोपरि होता है?",
          options: [
            "राउंडअबाउट में प्रवेश कर रहे वाहन",
            "राउंडअबाउट में पहले से मौजूद वाहन",
            "पैदल यात्री",
            "साइकिल चालक"
          ],
          correctAnswer: 1,
          explanation: "राउंडअबाउट में पहले से मौजूद वाहनों को प्राथमिकता होती है।"
        },
        {
          id: 7,
          question: "मोड़ लेने से पहले आपको क्या करना चाहिए?",
          options: [
            "हॉर्न बजाएं और गति बढ़ाएं",
            "धीमे चलें और मिरर देखें",
            "सीधा मोड़ लगाएं",
            "कुछ नहीं"
          ],
          correctAnswer: 1,
          explanation: "मोड़ लेने से पहले धीमे चलें और मिरर से पीछे देखें।"
        },
        {
          id: 8,
          question: "सड़क के बीच में पीली लाइन का क्या मतलब होता है?",
          options: [
            "पार्किंग की अनुमति है",
            "ओवरटेकिंग की अनुमति है",
            "ओवरटेकिंग नहीं है",
            "रोकना नहीं है"
          ],
          correctAnswer: 2,
          explanation: "लगातार पीली लाइन का मतलब ओवरटेकिंग या क्रॉसिंग मना है।"
        },
        {
          id: 9,
          question: "सीट बेल्ट है:",
          options: [
            "वैकल्पिक",
            "सिर्फ फ्रंट सीट के लिए आवश्यक",
            "सभी यात्रियों के लिए अनिवार्य",
            "शहर में आवश्यक नहीं"
          ],
          correctAnswer: 2,
          explanation: "सभी यात्रियों के लिए सीट बेल्ट पहनना अनिवार्य है।"
        },
        {
          id: 10,
          question: "यदि आपातकालीन वाहन सायरन के साथ आ रहा है, तो आपको क्या करना चाहिए?",
          options: [
            "गति बढ़ाएँ और दूर जाएं",
            "इग्नोर करें",
            "बाईं ओर चले और रुकें",
            "सड़क के बीच में रुकें"
          ],
          correctAnswer: 2,
          explanation: "आपातकालीन वाहन को पास करने दें, बाईं ओर हटें और रुकें।"
        },
        {
          id: 11,
          question: "ड्राइविंग करते समय आपको क्या साथ रखना चाहिए?",
          options: [
            "ड्राइविंग लाइसेंस",
            "मोबाइल फोन",
            "चॉकलेट",
            "धूप का चश्मा"
          ],
          correctAnswer: 0,
          explanation: "ड्राइविंग लाइसेंस साथ रखना अनिवार्य है।"
        },
        {
          id: 12,
          question: "हॉर्न कब उपयोग करना चाहिए?",
          options: [
            "अस्पताल के पास",
            "जरूरी होने पर दूसरों को चेताने के लिए",
            "चुप्पी क्षेत्र में ओवरटेकिंग के दौरान",
            "भारी ट्रैफिक में लगातार बजाना"
          ],
          correctAnswer: 1,
          explanation: "सिर्फ जरूरत होने पर हॉर्न बजाएं।"
        },
        {
          id: 13,
          question: "पैदल यात्री क्रॉसिंग (ज़ेब्रा क्रॉसिंग) किस लिए होती है?",
          options: [
            "कार रोकने के लिए",
            "पैदल यात्री को पार करने के लिए",
            "वाहन पार्क करने के लिए",
            "साइकिल चालकों के लिए"
          ],
          correctAnswer: 1,
          explanation: "यह पैदल यात्रियों को सड़क पार करने के लिए होता है।"
        },
        {
          id: 14,
          question: "हेलमेट पहनना किसके लिए अनिवार्य है?",
          options: [
            "कार ड्राइवर",
            "ट्रक चालक",
            "दोपहिया वाहन चालक",
            "बस कंडक्टर"
          ],
          correctAnswer: 2,
          explanation: "दोपहिया वाहन चालकों के लिए हेलमेट पहनना अनिवार्य है।"
        },
        {
          id: 15,
          question: "डबल सॉलिड लाइन का मतलब है:",
          options: [
            "ओवरटेकिंग की अनुमति है",
            "लाइन पार न करें",
            "गति बढ़ाएं",
            "हॉर्न बजाएँ"
          ],
          correctAnswer: 1,
          explanation: "डबल सॉलिड लाइन का मतलब पार या ओवरटेकिंग मना है।"
        },
        {
          id: 16,
          question: "रियर-व्यू मिरर का मुख्य उपयोग क्या है?",
          options: [
            "मेक-अप के लिए",
            "पीछे ट्रैफिक देखने के लिए",
            "धूप रोकने के लिए",
            "यात्री देखने के लिए"
          ],
          correctAnswer: 1,
          explanation: "यह पीछे की ट्रैफिक देखने के लिए होता है।"
        },
        {
          id: 17,
          question: "शराब पीकर ड्राइविंग करने पर दंड क्या हो सकता है?",
          options: [
            "जुर्माना",
            "कैद",
            "लाइसेंस निलंबन",
            "इनमें से सभी"
          ],
          correctAnswer: 3,
          explanation: "शराब पीकर ड्राइविंग पर जुर्माना, कैद और लाइसेंस निलंबन हो सकता है।"
        },
        {
          id: 18,
          question: "आप हॉर्न कब नहीं बजाना चाहिए?",
          options: [
            "रात में आवासीय क्षेत्रों में",
            "ओवरटेकिंग करते समय",
            "खतरे के लिए चेताने के लिए",
            "इनमें से कोई नहीं"
          ],
          correctAnswer: 0,
          explanation: "रात को आवासीय इलाकों में हॉर्न बजाना मना है।"
        },
        {
          id: 19,
          question: "हैंडब्रेक का मुख्य उपयोग क्या है?",
          options: [
            "गति बढ़ाने के लिए",
            "वाहन पार्क करने के लिए",
            "मोड़ने के लिए",
            "ओवरटेकिंग के लिए"
          ],
          correctAnswer: 1,
          explanation: "हैंडब्रेक वाहन को पार्क करते समय लगाते हैं।"
        },
        {
          id: 20,
          question: "यदि स्कूल बस सड़क पर रुकी हो, तो आपको क्या करना चाहिए?",
          options: [
            "हॉर्न बजायें और आगे बढ़ें",
            "रुकें जब तक बच्चे पार न हो जाएं",
            "तेजी से ओवरटेक करें",
            "नज़रअंदाज़ करें और जाएं"
          ],
          correctAnswer: 1,
          explanation: "स्कूल बस रुकी हो और बच्चे पार कर रहे हों तो रुकना अनिवार्य है।"
        }
      ]
    },
    {
      id: 2,
      title: "बुनियादी नियम & चिन्ह",
      difficulty: "easy",
      icon: "📙",
      totalQuestions: 20,
      questions: [
        {
          id: 1,
          question: "स्टॉप साइन का क्या अर्थ होता है?",
          options: [
            "रुकें",
            "जाएँ",
            "सुरक्षित होने पर रुकें और आगे बढ़ें",
            "गति बढ़ाएँ"
          ],
          correctAnswer: 2,
          explanation: "स्टॉप साइन का मतलब है पूरी तरह रुकें, फिर आगे बढ़ें।"
        },
        {
          id: 2,
          question: "'नो पार्किंग' साइन का मतलब क्या है?",
          options: [
            "कोई स्टॉपिंग नहीं",
            "नो एंट्री",
            "उस स्थान पर गाड़ी पार्क न करें",
            "स्पीड लिमिट"
          ],
          correctAnswer: 2,
          explanation: "नो पार्किंग का मतलब उस स्थान पर वाहन पार्किंग मना है।"
        },
        {
          id: 3,
          question: "चलती गाड़ी के पीछे न्यूनतम दूरी क्या होनी चाहिए?",
          options: [
            "कम से कम 10 मीटर",
            "1 मीटर",
            "1 कार की लंबाई",
            "कोई भी दूरी"
          ],
          correctAnswer: 0,
          explanation: "सुरक्षा के लिए कम से कम 10 मीटर दूरी रखें।"
        },
        {
          id: 4,
          question: "लाल बॉर्डर वाला त्रिकोणीय साइन आमतौर पर क्या दर्शाता है?",
          options: [
            "अनिवार्य साइन",
            "सावधानी साइन",
            "जानकारी देने वाला साइन",
            "कुछ नहीं"
          ],
          correctAnswer: 1,
          explanation: "लाल बॉर्डर वाला त्रिकोण सावधानी संकेत होता है।"
        },
        {
          id: 5,
          question: "ओवरटेकिंग कहाँ मना है?",
          options: [
            "मुड़ों पर",
            "ब्रिज पर",
            "चौराहों पर",
            "इन सभी जगहों पर"
          ],
          correctAnswer: 3,
          explanation: "सुरक्षा के लिए इन सभी जगह ओवरटेकिंग मना है।"
        },
        {
          id: 6,
          question: "तड़कती पीली ट्रैफिक सिग्नल का मतलब क्या है?",
          options: [
            "पूरी तरह रुकें",
            "सावधानी से आगे बढ़ें",
            "तेज़ चलें",
            "हॉर्न बजाएं"
          ],
          correctAnswer: 1,
          explanation: "तड़कती पीली का मतलब सावधानी से चलना।"
        },
        {
          id: 7,
          question: "पैदल यात्री क्रॉसिंग कैसे चिन्हित होती है?",
          options: [
            "कैट आईज",
            "सफेद धारीदार रेखाएँ",
            "लाल त्रिकोण",
            "बाड़"
          ],
          correctAnswer: 1,
          explanation: "ज़ेब्रा क्रॉसिंग सफेद धारियों से चिन्हित होती है।"
        },
        {
          id: 8,
          question: "आवासीय क्षेत्रों में सामान्य गति सीमा क्या है?",
          options: [
            "60 किमी/घंटा",
            "10 किमी/घंटा",
            "25 किमी/घंटा",
            "कोई सीमा नहीं"
          ],
          correctAnswer: 2,
          explanation: "आवासीय क्षेत्र में गति सीमा 25 किमी/घंटा होती है।"
        },
        {
          id: 9,
          question: "बिना गेट वाले रेलवे क्रॉसिंग पर कैसे चलना चाहिए?",
          options: [
            "तेजी से पार करें",
            "दोनों ओर देखें और तभी पार करें",
            "हॉर्न बजाएं और पार करें",
            "बीच में रुकें"
          ],
          correctAnswer: 1,
          explanation: "दोनों दिशाओं को देखकर ही सुरक्षित रूप से क्रॉसिंग करें।"
        },
        {
          id: 10,
          question: "मुख्य सड़क पर जुड़ने के लिए सुरक्षित तरीका क्या है?",
          options: [
            "पीछे रिवर्स में आएं",
            "ट्रैफिक देखें और धीरे-धीरे आगे बढ़ें",
            "हॉर्न बजाएं और जल्दी जाएं",
            "किसी के इशारे पर जाएं"
          ],
          correctAnswer: 1,
          explanation: "ट्रैफिक चेक करें फिर धीरे से मुख्य सड़क पर आएं।"
        },
        {
          id: 11,
          question: "जानकारी देने वाले साइन का रंग क्या होता है?",
          options: [
            "नीला",
            "लाल",
            "हरा",
            "सफेद"
          ],
          correctAnswer: 0,
          explanation: "भारत में सूचना साइन नीले रंग के होते हैं।"
        },
        {
          id: 12,
          question: "आपको हेडलाइट कब जलानी चाहिए?",
          options: [
            "सिर्फ बारिश में",
            "हमेशा",
            "सूर्यास्त से सूर्योदय तक या कम दृश्यता में",
            "कभी नहीं"
          ],
          correctAnswer: 2,
          explanation: "रात और कम दृश्यता में हेडलाइट का प्रयोग करें।"
        },
        {
          id: 13,
          question: "'नो एंट्री' साइन कैसा होता है?",
          options: [
            "लाल सर्कल जिसमें सफेद क्षैतिज रेखा हो",
            "हरा वर्ग",
            "नीला आयत",
            "पीला त्रिकोण"
          ],
          correctAnswer: 0,
          explanation: "लाल सर्कल और सफेद लाइन नो एंट्री दर्शाती है।"
        },
        {
          id: 14,
          question: "कहाँ पार्किंग मना है?",
          options: [
            "संक्रमण के पास",
            "फ्लायओवर पर",
            "बस स्टॉप के पास",
            "सभी ऊपर बताए गए स्थानों पर"
          ],
          correctAnswer: 3,
          explanation: "किसी भी चौराहे, फ्लायओवर या बस स्टॉप के पास पार्किंग मना है।"
        },
        {
          id: 15,
          question: "ट्रैफिक सिग्नल न होने पर दो वाहन कैसे निर्णय लें कि कौन पहले जाएगा?",
          options: [
            "दाईं तरफ वाला पहले जाए",
            "बाईं तरफ वाला पहले जाए",
            "जो पहले पहुंचे वही जाएगा",
            "बड़ा वाहन पहले जाएगा"
          ],
          correctAnswer: 0,
          explanation: "दाईं तरफ वाला वाहन पहले चलता है।"
        },
        {
          id: 16,
          question: "'यू टर्न प्रोहिबिटेड' साइन का अर्थ क्या है?",
          options: [
            "हमेशा यू टर्न कर सकते हैं",
            "सावधानी से यू टर्न कर सकते हैं",
            "उस स्थान पर यू टर्न नहीं कर सकते",
            "ओवरटेकिंग की अनुमति है"
          ],
          correctAnswer: 2,
          explanation: "यू टर्न मना होने का मतलब है यू टर्न न करें।"
        },
        {
          id: 17,
          question: "कब आपको हेडलाइट डिप करनी चाहिए?",
          options: [
            "दिन में",
            "जब कोई दूसरी गाड़ी सामने आये",
            "कभी नहीं",
            "हाइवे पर"
          ],
          correctAnswer: 1,
          explanation: "दूसरी गाड़ी के लिए चालकों की आंखों में रौशनी न जाए इसके लिए डिप करें।"
        },
        {
          id: 18,
          question: "वाहन में सामान कैसे रखे जाने चाहिए?",
          options: [
            "कोई प्रतिबंध नहीं",
            "सिर्फ साइड से बाहर निकला हो",
            "सड़क पर अवरोध या खतरनाक आकार में बाहर न निकले",
            "पीछे बाहर निकला हो"
          ],
          correctAnswer: 2,
          explanation: "सामान ऐसी तरह रखा जाए कि सफर सुरक्षित हो और सड़क अवरुद्ध न हो।"
        },
        {
          id: 19,
          question: "कौन सा दस्तावेज वाहन में रखना अनिवार्य नहीं है?",
          options: [
            "ड्राइविंग लाइसेंस",
            "वाहन पंजीकरण कागजात",
            "मोबाइल रिचार्ज रसीद",
            "बीमा कागजात"
          ],
          correctAnswer: 2,
          explanation: "मोबाइल रिचार्ज रसीद रखने की कोई कानूनी जरूरत नहीं है।"
        },
        {
          id: 20,
          question: "ट्रैफिक सिग्नल पर ग्रीन एरो का क्या मतलब है?",
          options: [
            "सिर्फ उस दिशा में जाएं जैसा दिखाया गया हो",
            "रुकें",
            "इंतजार करें",
            "किसी भी दिशा में जा सकते हैं"
          ],
          correctAnswer: 0,
          explanation: "ग्रीन एरो दर्शाता है केवल उस दिशा में आगे बढ़ें।"
        }
      ]
    },
    {
      id: 3,
      title: "सड़क सुरक्षा",
      difficulty: "easy",
      icon: "🦺",
      totalQuestions: 20,
      questions: [
        {
          id: 1,
          question: "यदि ड्राइविंग करते समय नींद आये तो क्या करें?",
          options: [
            "खिड़कियाँ खोलें",
            "सुरक्षित स्थान पर रुकें",
            "सिर्फ कॉफी पियें",
            "धीमे चलाते रहें"
          ],
          correctAnswer: 1,
          explanation: "नींद आये तो हमेशा सुरक्षित जगह रुककर आराम करें।"
        },
        {
          id: 2,
          question: "सुरक्षित यात्रा के लिए सीट बेल्ट कौन पहनें?",
          options: [
            "सिर्फ फ्रंट सीट वाले",
            "सिर्फ ड्राइवर",
            "सिर्फ पिछली सीट वाले",
            "सभी यात्रियों को"
          ],
          correctAnswer: 3,
          explanation: "सभी यात्रियों को सीट बेल्ट पहनना जरूरी है।"
        },
        {
          id: 3,
          question: "हाइड्रोप्लैनिंग तब होता है जब:",
          options: [
            "सूखे मौसम में धीरे चलना",
            "गीली सतह पर तेज गति से चलना",
            "सूखे रास्ते पर जोर से ब्रेक लगाना",
            "भारी ट्रैफिक में धीरे चलना"
          ],
          correctAnswer: 1,
          explanation: "गीली सतह पर तेज चलने से पहिया फिसलता है जिसे हाइड्रोप्लैनिंग कहते हैं।"
        },
        {
          id: 4,
          question: "ओवरटेकिंग से पहले किसका ध्यान रखें?",
          options: [
            "पीछे की गाड़ियाँ",
            "आने वाली ट्रैफिक",
            "ट्रैफिक सिग्नल",
            "दोनों A और B"
          ],
          correctAnswer: 3,
          explanation: "ओवरटेकिंग से पहले हमेशा पीछे और सामने ट्रैफिक देखें।"
        },
        {
          id: 5,
          question: "पैदल यात्री क्रॉसिंग पर आपको क्या करना चाहिए?",
          options: [
            "गति बढ़ाएं",
            "पैदल यात्रियों के लिए रुकें",
            "हॉर्न बजाएं और चलें",
            "सिर्फ पुलिस होने पर रुकें"
          ],
          correctAnswer: 1,
          explanation: "पैदल यात्रियों के लिए क्रॉसिंग पर रुकना जरूरी है।"
        },
        {
          id: 6,
          question: "रात में हल्के रंग के कपड़े पहनना:",
          options: [
            "फैशन है",
            "पैदल यात्री को दिखाई देने में मदद करता है",
            "ड्राइवर का ध्यान भटकाता है",
            "सही नहीं"
          ],
          correctAnswer: 1,
          explanation: "रात में हल्के कपड़े पहनना ड्राइवर को पैदल यात्री दिखाने में मदद करता है।"
        },
        {
          id: 7,
          question: "सड़क पर जानवर दिखें तो:",
          options: [
            "हॉर्न बजाएं और जल्दी निकलें",
            "धीमे चलें और तैयार रहें रुकने को",
            "नजरअंदाज करें",
            "तेज चलायें"
          ],
          correctAnswer: 1,
          explanation: "जानवरों को देखकर धीमे चलें और जरूरत पड़े तो रुकें।"
        },
        {
          id: 8,
          question: "रेलवे क्रॉसिंग के गेट खुले हों तो क्या करें?",
          options: [
            "जल्दी से पार हो जाएं",
            "दोनों तरफ देखें और सुरक्षित पार करें",
            "हॉर्न बजाएं और पार करें",
            "बीच में रुकें"
          ],
          correctAnswer: 1,
          explanation: "गेट खुले होने पर भी दोनों तरफ देखकर ही पार करें।"
        },
        {
          id: 9,
          question: "कोहरे में ड्राइविंग के लिए सही तरीका क्या है?",
          options: [
            "हाई बीम लाइट जलाना",
            "लो बीम हेडलाइट या फॉग लैंप का उपयोग",
            "लाइट बंद करना",
            "केवल हैज़र्ड लाइट जलाना"
          ],
          correctAnswer: 1,
          explanation: "कोहरे में लो बीम या फॉग लैंप जलाना सबसे अच्छा होता है।"
        },
        {
          id: 10,
          question: "ड्राइविंग करते समय मोबाइल फोन का उपयोग क्यों खतरनाक है?",
          options: [
            "ध्यान भटकता है",
            "कोई प्रभाव नहीं",
            "ध्यान केंद्रित करने में मदद करता है",
            "तेज ड्राइविंग संभव होती है"
          ],
          correctAnswer: 0,
          explanation: "मोबाइल उपयोग से ध्यान भटकता है जिससे दुर्घटना हो सकती है।"
        },
        {
          id: 11,
          question: "कार में कीमती सामान सबसे सुरक्षित कहां रखें?",
          options: [
            "ताला लगा दस्तावेज़ बॉक्स में",
            "डैशबोर्ड पर",
            "पीछे की सीट पर",
            "मिरर पर टंगे हुए"
          ],
          correctAnswer: 0,
          explanation: "सामान ताले लगे दस्तावेज़ बॉक्स में रखें ताकि चोरी से बचाव हो।"
        },
        {
          id: 12,
          question: "स्किडिंग कैसे रोकी जा सकती है?",
          options: [
            "चिपचिपा ब्रेक लगाना",
            "तेज गति से",
            "धीरे और नियंत्रित ब्रेक और एक्सेलरेटर का उपयोग",
            "भीषण भार"
          ],
          correctAnswer: 2,
          explanation: "धीरे और नियंत्रित तरीके से ब्रेक और एक्सेलरेटर का उपयोग करें।"
        },
        {
          id: 13,
          question: "12 वर्ष से कम उम्र के बच्चे कहाँ बैठें?",
          options: [
            "सामने",
            "पीछे बैठकर सीट बेल्ट पहनें",
            "खड़े होकर",
            "गदद पर"
          ],
          correctAnswer: 1,
          explanation: "बच्चों को पीछे सीट में और सीट बेल्ट पहनकर बैठाना चाहिए।"
        },
        {
          id: 14,
          question: "मोड़ते समय इंडिकेटर का उपयोग क्यों जरूरी है?",
          options: [
            "जरूरी नहीं",
            "दूसरों को सूचना देने के लिए आवश्यक",
            "सिर्फ हाइवे पर",
            "मूड अनुसार"
          ],
          correctAnswer: 1,
          explanation: "मोड़ते समय इंडिकेटर देना जरूरी होता है ताकि अन्य वाहन चालकों को पता चले।"
        },
        {
          id: 15,
          question: "ब्रेक फेल होने पर सबसे पहले क्या करें?",
          options: [
            "गति बढ़ाएं",
            "नीची गियर डालें और हैंडब्रेक धीरे लगाएं",
            "कार से कूद जाएं",
            "सिर्फ हॉर्न बजाएं"
          ],
          correctAnswer: 1,
          explanation: "ब्रेक फेल होने पर नीची गियर डालकर हैंडब्रेक का धीरे उपयोग करें।"
        },
        {
          id: 16,
          question: "अधिकांश दुर्घटनाएँ कब होती हैं?",
          options: [
            "साफ मौसम में",
            "रात में कम दृश्यता में",
            "लंबे समय बाद बारिश शुरू होते ही",
            "जब पुलिस मौजूद हो"
          ],
          correctAnswer: 2,
          explanation: "बारिश के पहले बादल जमने पर सड़क फिसलन भरी हो जाती है।"
        },
        {
          id: 17,
          question: "पीले डायमंड के आकार का साइन क्या सूचित करता है?",
          options: [
            "अनिवार्य आदेश",
            "सावधानी/चेतावनी",
            "सूचना",
            "खेल का मैदान"
          ],
          correctAnswer: 1,
          explanation: "पीला डायमंड साइन चेतावनी देता है।"
        },
        {
          id: 18,
          question: "ब्लाइंड स्पॉट का क्या अर्थ है?",
          options: [
            "विंडशील्ड वाइपर साफ करते हैं",
            "ऐसे क्षेत्र जो मिरर में नहीं दिखते",
            "कार में नहीं होते",
            "सिर्फ ट्रकों के लिए खतरनाक"
          ],
          correctAnswer: 1,
          explanation: "ब्लाइंड स्पॉट वे क्षेत्र होते हैं जो मिरर से दिखाई नहीं देते।"
        },
        {
          id: 19,
          question: "पुराने टायर उपयोग करने से क्या होता है?",
          options: [
            "गति बढ़ती है",
            "दुर्घटना का खतरा कम होता है",
            "खतरनाक होता है और पकड़ कम होती है",
            "कोई असर नहीं होता"
          ],
          correctAnswer: 2,
          explanation: "पुराने टायर से सड़क पकड़ कम होती है और दुर्घटना का खतरा बढ़ता है।"
        },
        {
          id: 20,
          question: "परफेक्ट कंट्रोल के लिए स्टीयरिंग पर हाथ रखना कैसा चाहिए?",
          options: [
            "12 बजे की स्थिति पर",
            "10 और 2 या 9 और 3 बजे की स्थिति पर",
            "गोल पकड़कर",
            "अधिकतर एक तरफ"
          ],
          correctAnswer: 1,
          explanation: "स्टीयरिंग कंट्रोल के लिए 10-2 या 9-3 बजे की स्थिति में दोनों हाथ रखें।"
        }
      ]

    },
    {
      id: 4,
      title: "साइन पहचान",
      difficulty: "easy",
      icon: "🔰",
      totalQuestions: 20,
      questions: [
        {
          id: 1,
          question: "नीला वर्गाकार साइन आमतौर पर क्या दर्शाता है?",
          options: [
            "जानकारी",
            "सावधानी",
            "प्रतिबंध",
            "कुछ नहीं"
          ],
          correctAnswer: 0,
          explanation: "नीला वर्गाकार साइन सूचना देने वाले चिन्ह होते हैं।"
        },
        {
          id: 2,
          question: "लाल सर्कल जिसमें सफेद क्षैतिज बार हो, इसका क्या मतलब होता है?",
          options: [
            "नो एंट्री",
            "खतरा आगे",
            "पार्किंग की अनुमति",
            "सिर्फ बाएं मुड़ना"
          ],
          correctAnswer: 0,
          explanation: "लाल सर्कल में सफेद बार का मतलब नो एंट्री है।"
        },
        {
          id: 3,
          question: "लाल सर्कल में 30 लिखा हुआ साइन का मतलब क्या है?",
          options: [
            "30 किमी/घंटा न्यूनतम गति",
            "30 किमी/घंटा गति सीमा",
            "30 वाहनों की अनुमति",
            "30 मीटर बाद तेज मोड़"
          ],
          correctAnswer: 1,
          explanation: "लाल सर्कल के अंदर संख्या गति सीमा बताती है।"
        },
        {
          id: 4,
          question: "पीले त्रिकोण पर काला क्रॉस चिह्न क्या दर्शाता है?",
          options: [
            "अस्पताल आगे",
            "क्रॉस रोड आगे",
            "टी-जंक्शन",
            "रेलवे क्रॉसिंग"
          ],
          correctAnswer: 1,
          explanation: "पीले त्रिकोण में काला क्रॉस रोड के आने की चेतावनी देता है।"
        },
        {
          id: 5,
          question: "नीले आयताकार में ‘H’ का साइन क्या दर्शाता है?",
          options: [
            "हाइवे",
            "अस्पताल आगे",
            "रुकें",
            "हेलिपैड"
          ],
          correctAnswer: 1,
          explanation: "नीले आयत में ‘H’ का मतलब अस्पताल आगे होता है।"
        },
        {
          id: 6,
          question: "‘नो हॉर्न’ का साइन किस प्रकार का होता है?",
          options: [
            "अनिवार्य",
            "सावधानी",
            "सूचनात्मक",
            "कुछ नहीं"
          ],
          correctAnswer: 0,
          explanation: "नो हॉर्न अनिवार्य संकेतक होता है।"
        },
        {
          id: 7,
          question: "बाएं मुड़े हुए तीर वाला साइन क्या बताता है?",
          options: [
            "नो पार्किंग",
            "बाएं मोड़ आगे",
            "बाएं से ओवरटेक करें",
            "सीधा जाएं"
          ],
          correctAnswer: 1,
          explanation: "मुड़ा हुआ तीर मोड़ आने की सूचना देता है।"
        },
        {
          id: 8,
          question: "स्कूल जाने वाले बच्चे की तस्वीर वाला साइन क्या दर्शाता है?",
          options: [
            "खेल का मैदान आगे",
            "स्कूल जोन आगे",
            "बच्चों के लिए नो एंट्री",
            "चिड़ियाघर आगे"
          ],
          correctAnswer: 1,
          explanation: "स्कूल जोन साइन बच्चों के पार होने के लिए चेतावनी देता है।"
        },
        {
          id: 9,
          question: "त्रिकोण में मोटी खड़ी पट्टी और दोनों ओर दो पतली रेखाएं क्या दर्शाती हैं?",
          options: [
            "संकरा पुल आगे",
            "चौड़ा सड़क",
            "ओवरटेकिंग मना है",
            "रेलवे क्रॉसिंग"
          ],
          correctAnswer: 0,
          explanation: "यह साइन संकरा पुल या संकरी सड़क पर सावधानी के लिए होता है।"
        },
        {
          id: 10,
          question: "दायें मुड़े हुए तीर वाला गोल साइन जिसपर क्रॉस हो, मतलब है?",
          options: [
            "दायाँ मुड़ना मना है",
            "दायाँ मुड़ना अनिवार्य है",
            "दायें पार्किंग",
            "गति बढ़ाएं"
          ],
          correctAnswer: 0,
          explanation: "क्रॉस वाला तीर उस दिशा में मुड़ना मना करता है।"
        },
        {
          id: 11,
          question: "त्रिकोण में टीला (बंप) की छवि क्या चेतावनी देती है?",
          options: [
            "पत्थर गिरने का खतरा",
            "सर्प पथ बाधा (स्पीड ब्रेकर)",
            "निम्न पुल",
            "सुरंग आगे"
          ],
          correctAnswer: 1,
          explanation: "टीला या बंप स्पीड ब्रेकर का संकेत देता है।"
        },
        {
          id: 12,
          question: "लाल सर्कल में 'P' पर क्रॉस का क्या मतलब है?",
          options: [
            "पार्किंग उपलब्ध",
            "पार्किंग की अनुमति",
            "पार्किंग मना है",
            "प्राथमिकता पार्किंग"
          ],
          correctAnswer: 2,
          explanation: "'P' पर क्रॉस का मतलब नो पार्किंग होता है।"
        },
        {
          id: 13,
          question: "नीले आयताकार में सफेद बस प्रतीक क्या दर्शाता है?",
          options: [
            "बस लेन",
            "बस स्टैंड/स्टॉप",
            "बसें प्रवेश निषेध",
            "बस पार्किंग"
          ],
          correctAnswer: 1,
          explanation: "यह बस स्टॉप दर्शाता है।"
        },
        {
          id: 14,
          question: "लाल सर्कल में ट्रक की छवि किसका संकेत देती है?",
          options: [
            "भारी वाहन की अनुमति",
            "ट्रकों को धीमे चलना चाहिए",
            "भारी वाहनों के लिए नो एंट्री",
            "ट्रक पार्किंग"
          ],
          correctAnswer: 2,
          explanation: "यह भारी वाहनों के लिए प्रवेश निषेध दर्शाता है।"
        },
        {
          id: 15,
          question: "साइकिल छवि पर क्रॉस वाला वृत्त क्या दर्शाता है?",
          options: [
            "साइकिल स्टैंड",
            "साइकिलों के लिए नो एंट्री",
            "साइकिल चालकों के लिए",
            "साइकिल मरम्मत"
          ],
          correctAnswer: 1,
          explanation: "साइकिल पर क्रॉस का मतलब साइकल जाना मना है।"
        },
        {
          id: 16,
          question: "कार और बाइक के चित्र वाला साइन क्या दर्शाता है?",
          options: [
            "फ्री पार्किंग",
            "दोनों वाहन अनुमति हैं",
            "कोई भी वाहन ओवरटेक कर सकता है",
            "बिना ट्रैफिक सिग्नल वाले चौराहा"
          ],
          correctAnswer: 3,
          explanation: "यह साइन बिना नियंत्रण वाले क्रॉसिंग का संकेत है, सावधानी रखें।"
        },
        {
          id: 17,
          question: "लाल बॉर्डर के आयताकार साइन पर काले डॉट पैटर्न का मतलब?",
          options: [
            "पत्थर गिरने का खतरा",
            "फिसलन भरी सड़क",
            "खरादा (बंप) सड़क",
            "अवरोधित सड़क"
          ],
          correctAnswer: 1,
          explanation: "काले डॉट्स अक्सर फिसलन वाली सड़क को दर्शाते हैं।"
        },
        {
          id: 18,
          question: "दौर वाले सर्कल में सफेद तीर जो लूप बनाते हैं, उनका क्या मतलब है?",
          options: [
            "शुरुआत",
            "नो एंट्री",
            "राउंडअबाउट आगे",
            "दायें मुड़ना"
          ],
          correctAnswer: 2,
          explanation: "गोल तीर राउंडअबाउट को दर्शाते हैं।"
        },
        {
          id: 19,
          question: "कार के पीछे लहराते हुए लाइनों वाला साइन क्या दिखाता है?",
          options: [
            "नजदीक में कार वाश",
            "फिसलन भरी सड़क आगे",
            "गति बढ़ाएं",
            "अच्छी सड़कों वाला क्षेत्र"
          ],
          correctAnswer: 1,
          explanation: "यह फिसलन भरी सड़क की चेतावनी है।"
        },
        {
          id: 20,
          question: "ढलान पर ट्रक वाला साइन क्या दिखाता है?",
          options: [
            "हिल स्टेशन",
            "तीव्र ढलान आगे",
            "ट्रक स्टॉप",
            "ओवरटेकिंग अनुमति"
          ],
          correctAnswer: 1,
          explanation: "ढलान पर ट्रक साइन तीव्र ढलान की सूचना देता है।"
        }
      ]
    },
    {
      id: 5,
      title: "शुरुआती परीक्षा",
      difficulty: "easy",
      icon: "📝",
      totalQuestions: 20,
      questions: [
        {
          id: 1,
          question: "जब ट्रैफिक लाइट पीली होती है तो आपको क्या करना चाहिए?",
          options: [
            "लाल होने से पहले तेजी से पार हों",
            "रुकने के लिए तैयार रहें",
            "सिर्फ पुलिस के लिए रुकें",
            "कुछ न करें"
          ],
          correctAnswer: 1,
          explanation: "पीली लाइट का मतलब रुकने के लिए तैयार होना है।"
        },
        {
          id: 2,
          question: "रिवर्स गियर कब उपयोग करना चाहिए?",
          options: [
            "पैदल यात्री क्रॉसिंग पर",
            "पार्किंग या सुधार के लिए पीछे जाना",
            "मज़े के लिए कहीं भी",
            "मुख्य सड़क पर"
          ],
          correctAnswer: 1,
          explanation: "रिवर्स गियर केवल पीछे हटने या पार्किंग सुधार के लिए इस्तेमाल करें, मुख्य सड़क पर कभी नहीं।"
        },
        {
          id: 3,
          question: "अपना वाहन चालू करने से पहले आपको क्या करना चाहिए?",
          options: [
            "कुछ नहीं",
            "मिरर्स, सीट बेल्ट और आसपास की जाँच करें",
            "रेडियो चालू करें",
            "तुरंत गति बढ़ाएँ"
          ],
          correctAnswer: 1,
          explanation: "हमेशा मिरर देखें और सीट बेल्ट लगाएं।"
        },
        {
          id: 4,
          question: "अगर इन्डिकेटर काम नहीं कर रहे हों तो मोड़ का संकेत कैसे देंगे?",
          options: [
            "हाथ के संकेत दें",
            "संकेत देने की जरूरत नहीं",
            "जोर से चिल्लाएँ",
            "लाइट बंद करें"
          ],
          correctAnswer: 0,
          explanation: "इन्डिकेटर न काम करने पर निर्धारित हाथ के संकेत का उपयोग करें।"
        },
        {
          id: 5,
          question: "अगर आपको रात में पार्किंग करनी हो तो क्या करें?",
          options: [
            "कोई सावधानी नहीं बरतनी चाहिए",
            "अंधेरी जगह में पार्क करें",
            "पार्किंग लाइट जलाएं",
            "चौराहे पर पार्क करें"
          ],
          correctAnswer: 2,
          explanation: "रात में दृश्यता के लिए हमेशा पार्किंग लाइट जलाएं।"
        },
        {
          id: 6,
          question: "क्या आप साइलेंट जोन में हॉर्न का इस्तेमाल कर सकते हैं?",
          options: [
            "हाँ, हमेशा",
            "नहीं, प्रतिबंधित है",
            "जब मन करे तभी",
            "सिर्फ बारिश में"
          ],
          correctAnswer: 1,
          explanation: "साइलेंट जोन जैसे अस्पताल और स्कूल के पास हॉर्न बजाना मना है।"
        },
        {
          id: 7,
          question: "सड़क पर टूटी हुई सफेद लाइन का क्या मतलब है?",
          options: [
            "अगर सुरक्षित हो तो लेन बदला या ओवरटेक कर सकते हैं",
            "ओवरटेकिंग मना है",
            "नो एंट्री",
            "सिर्फ मोटरसाइकिल चलाने वालों के लिए"
          ],
          correctAnswer: 0,
          explanation: "टूटी सफेद लाइन पर सुरक्षित होने पर लेन बदली जा सकती है।"
        },
        {
          id: 8,
          question: "अगर टायर अचानक फट जाए तो आपको क्या करना चाहिए?",
          options: [
            "तकती ब्रेक लगाएं",
            "गति बढ़ाएं",
            "स्टीयरिंग संभालें और धीरे-धीरे धीमा करें",
            "फौरन मोड़ लें"
          ],
          correctAnswer: 2,
          explanation: "टायर फटने पर धीरे-धीरे गति कम करें और नियंत्रण बनाए रखें।"
        },
        {
          id: 9,
          question: "निम्नलिखित में से कौन मान्य ड्राइविंग लाइसेंस नहीं है?",
          options: [
            "मियाद समाप्त लाइसेंस",
            "मान्य भारतीय स्मार्ट कार्ड लाइसेंस",
            "डिजिटल रूप से हस्ताक्षरित लाइसेंस",
            "आरटीओ से ऑनलाइन प्रिंट किया हुआ लाइसेंस"
          ],
          correctAnswer: 0,
          explanation: "समय समाप्त लाइसेंस वैध नहीं होता।"
        },
        {
          id: 10,
          question: "हाइवे पर मर्ज (जुड़ने) के लिए आपको क्या करना चाहिए?",
          options: [
            "अचानक 속गी बढ़ाएँ",
            "गति बढ़ाएँ, ट्रैफिक देखें और सुरक्षित समय पर शामिल हों",
            "हॉर्न बजाएं और घुस जाएं",
            "रिवर्स लें"
          ],
          correctAnswer: 1,
          explanation: "गति मिलाएँ और ट्रैफिक साफ होने पर धीरे से मुख्य सड़क पर आएं।"
        },
        {
          id: 11,
          question: "एक निजी कार की नंबर प्लेट का रंग क्या होता है?",
          options: [
            "काले पृष्ठभूमि पर सफेद अक्षर",
            "सफेद पृष्ठभूमि पर काले अक्षर",
            "पीला पृष्ठभूमि",
            "लाल"
          ],
          correctAnswer: 1,
          explanation: "निजी कार की नंबर प्लेट सफेद पृष्ठभूमि पर काले अक्षरों वाली होती है।"
        },
        {
          id: 12,
          question: "बिना गेट वाली रेलवे क्रॉसिंग का साइन कैसा होता है?",
          options: [
            "दो पटरी वाला सर्कल",
            "रेलवे लाइनों वाला पीला त्रिकोण",
            "लाल X",
            "नीला वर्ग"
          ],
          correctAnswer: 1,
          explanation: "रेलवे लाइनों वाला त्रिकोण बिना गेट वाली क्रॉसिंग दर्शाता है।"
        },
        {
          id: 13,
          question: "मोटर वाहनों में साइलेंसर का क्या उपयोग है?",
          options: [
            "रेडियो के लिए",
            "इंजन की आवाज कम करने के लिए",
            "गति बढ़ाने के लिए",
            "तापमान कम करने के लिए"
          ],
          correctAnswer: 1,
          explanation: "साइलेंसर इंजन और एग्जॉस्ट की आवाज कम करता है।"
        },
        {
          id: 14,
          question: "मोटरिंग जोन को कैसे दर्शाया जाता है?",
          options: [
            "नीली सूचना बोर्ड",
            "पीला वृत्त",
            "हरा आयत",
            "लाल त्रिकोण"
          ],
          correctAnswer: 0,
          explanation: "मोटरिंग जोन में नीला सूचना बोर्ड होता है।"
        },
        {
          id: 15,
          question: "एकतरफा सड़क पार करते समय क्या देखें?",
          options: [
            "सिर्फ एक तरफ देखें",
            "दोनों तरफ देखें",
            "देखने की आवश्यकता नहीं",
            "तेजी से दौड़ें"
          ],
          correctAnswer: 1,
          explanation: "किसी भी सड़क को पार करने से पहले दोनों तरफ देखें।"
        },
        {
          id: 16,
          question: "'STOP' लिखे सड़क चिह्न का मतलब क्या होता है?",
          options: [
            "रुकने की आवश्यकता नहीं",
            "पूरी तरह रुकें और केवल जब सुरक्षित हो आगे बढ़ें",
            "गति बढ़ाएँ",
            "पीछे वाले ड्राइवर को देखें"
          ],
          correctAnswer: 1,
          explanation: "रुकें और सुनिश्चित करें कि आगे बढ़ना सुरक्षित है।"
        },
        {
          id: 17,
          question: "राउंडअबाउट के नियम क्या हैं?",
          options: [
            "दाईं ओर से आने वाले ट्रैफिक को रास्ता दें",
            "हमेशा अंदर बढ़ाएँ",
            "सभी मोड़ के लिए बाईं लेन का उपयोग करें",
            "राउंडअबाउट के अंदर ओवरटेक करें"
          ],
          correctAnswer: 0,
          explanation: "राउंडअबाउट में प्रवेश करते समय दाईं ओर से आने वाले ट्रैफिक को रास्ता देना चाहिए।"
        },
        {
          id: 18,
          question: "कोहरे में ड्राइविंग कैसे करनी चाहिए?",
          options: [
            "लाइट बंद रखें",
            "तेज़ ड्राइव करें",
            "फॉग लैंप का उपयोग करें और धीरे चलें",
            "बार-बार हॉर्न बजाएं"
          ],
          correctAnswer: 2,
          explanation: "कोहरे में फॉग लैंप जलाएं, धीरे और सावधानी से चलें।"
        },
        {
          id: 19,
          question: "वर्तमान मोटर वाहन अधिनियम कब लागू हुआ था?",
          options: [
            "1988",
            "2005",
            "2019",
            "1965"
          ],
          correctAnswer: 0,
          explanation: "मोटर वाहन अधिनियम, 1988 वर्तमान नियमों का आधार है।"
        },
        {
          id: 20,
          question: "दुर्घटना स्थल पर घायल व्यक्ति को पाएँ तो क्या करें?",
          options: [
            "तुरंत छोड़ दें",
            "मदद करें और आपातकालीन सेवाओं को कॉल करें",
            "सिर्फ फोटो लें",
            "दूसरों पर चिल्लाएँ"
          ],
          correctAnswer: 1,
          explanation: "हमेशा मदद करें और तुरंत आपातकालीन सेवाओं को सूचित करें।"
        }
      ]
    },
    {
      id: 6,
      title: "दंड और जुर्माने",
      difficulty: "intermediate",
      icon: "💸",
      totalQuestions: 20,
      questions: [
        {
          id: 1,
          question: "मोटर व्हीकल एक्ट (2019) के अनुसार सीट बेल्ट न पहनने पर दंड क्या है?",
          options: ["₹100", "₹500", "₹1000", "₹2000"],
          correctAnswer: 1,
          explanation: "सीट बेल्ट न पहनने पर ₹500 का जुर्माना है।"
        },
        {
          id: 2,
          question: "शराब पीकर ड्राइविंग करने पर दंड में क्या शामिल हो सकता है?",
          options: [
            "केवल जुर्माना",
            "जुर्माना और/या जेल",
            "लाइसेंस निलंबन",
            "B और C दोनों"
          ],
          correctAnswer: 3,
          explanation: "आप जुर्माना, लाइसेंस निलंबन और जेल दोनों का सामना कर सकते हैं।"
        },
        {
          id: 3,
          question: "ड्राइविंग करते समय मोबाइल फोन का उपयोग करने पर जुर्माना कितना है?",
          options: [
            "₹100",
            "पहली बार ₹2,000",
            "₹500",
            "₹10,000"
          ],
          correctAnswer: 1,
          explanation: "फोन इस्तेमाल करने पर पहली बार ₹2,000 और पुनरावृत्ति पर ₹10,000 जुर्माना है।"
        },
        {
          id: 4,
          question: "वैध लाइसेंस के बिना ड्राइविंग करने पर क्या दंड होता है?",
          options: ["चेतावनी", "₹5000 तक जुर्माना", "कोई कार्रवाई नहीं", "लाइसेंस नवीनीकरण"],
          correctAnswer: 1,
          explanation: "नए नियमों के तहत ₹5,000 तक का जुर्माना हो सकता है।"
        },
        {
          id: 5,
          question: "LMV (कार) की ओवर-स्पीडिंग पर दंड क्या होता है?",
          options: [
            "₹400",
            "₹1,000 - ₹2,000",
            "₹5,000",
            "केवल ₹1,000"
          ],
          correctAnswer: 1,
          explanation: "LMV की ओवर-स्पीडिंग पर ₹1,000 से ₹2,000 जुर्माना होता है।"
        },
        {
          id: 6,
          question: "आपातकालीन वाहनों को रास्ता न देने पर क्या परिणाम हो सकते हैं?",
          options: [
            "सिर्फ मौखिक चेतावनी",
            "₹10,000 तक जुर्माना",
            "लाइसेंस रद्द",
            "कोई नहीं"
          ],
          correctAnswer: 1,
          explanation: "संजीदा कार्रवाई और ₹10,000 तक का जुर्माना हो सकता है।"
        },
        {
          id: 7,
          question: "खतरनाक ड्राइविंग के दंड का प्रावधान किस सेक्शन में है?",
          options: [
            "एमवी एक्ट सेक्शन 177",
            "सेक्शन 184",
            "सेक्शन 185",
            "सेक्शन 202"
          ],
          correctAnswer: 1,
          explanation: "सेक्शन 184 खतरनाक ड्राइविंग से संबंधित है।"
        },
        {
          id: 8,
          question: "ड्राइविंग के दौरान यदि बीमा का प्रमाण नहीं है तो दंड क्या है?",
          options: [
            "₹500",
            "₹2,000/3 महीने जेल या दोनों",
            "₹100",
            "₹5,000"
          ],
          correctAnswer: 1,
          explanation: "₹2,000 का जुर्माना या 3 महीने जेल हो सकती है।"
        },
        {
          id: 9,
          question: "अंजीकृत वाहन चलाने पर जुर्माना कितना है?",
          options: [
            "₹500",
            "₹2,000",
            "₹5,000",
            "₹10,000"
          ],
          correctAnswer: 3,
          explanation: "अंजीकृत वाहन चलाने पर ₹10,000 का जुर्माना होता है।"
        },
        {
          id: 10,
          question: "यदि नाबालिग (18 वर्ष से कम) ड्राइविंग करते पाए जाएं तो क्या होगा?",
          options: [
            "कार को केवल टो किया जाएगा",
            "अभिभावक/मालिक दोषी माना जाएगा",
            "कोई कार्रवाई नहीं",
            "मौखिक चेतावनी"
          ],
          correctAnswer: 1,
          explanation: "कानून के अनुसार अभिभावक या वाहन मालिक जिम्मेदार होगा।"
        },
        {
          id: 11,
          question: "ट्रैफिक सिग्नल बार-बार तोड़ने पर दंड क्या है?",
          options: [
            "सिर्फ चेतावनी",
            "जुर्माना और लाइसेंस निरस्त हो सकता है",
            "सामुदायिक सेवा",
            "वाहन जब्ती"
          ],
          correctAnswer: 1,
          explanation: "बार-बार उल्लंघन से लाइसेंस निरस्तीकरण हो सकता है।"
        },
        {
          id: 12,
          question: "दो-पहिया वाहन पर हेलमेट न पहनने का दंड (2019) कितना है?",
          options: [
            "₹100",
            "₹500",
            "₹1,000",
            "₹200"
          ],
          correctAnswer: 1,
          explanation: "नए नियमों के अनुसार दंड ₹500 है।"
        },
        {
          id: 13,
          question: "प्रदूषण नियंत्रण नियम तोड़ने पर वाणिज्यिक वाहन का जुर्माना कितना है?",
          options: [
            "₹10,000 तक",
            "₹1,000",
            "₹50,000 तक",
            "₹250"
          ],
          correctAnswer: 0,
          explanation: "प्रदूषण नियम उल्लंघन पर ₹10,000 तक जुर्माना लगाया जा सकता है।"
        },
        {
          id: 14,
          question: "एम्बुलेंस या फायर ट्रक को रास्ता न देने पर जुर्माना कितना है?",
          options: [
            "₹2,000",
            "₹10,000",
            "₹100",
            "कोई जुर्माना नहीं"
          ],
          correctAnswer: 1,
          explanation: "रास्ता न देने पर ₹10,000 तक जुर्माना लग सकता है।"
        },
        {
          id: 15,
          question: "बार-बार खतरनाक ड्राइविंग करने पर क्या हो सकता है?",
          options: [
            "लाइसेंस निलंबन",
            "कैद",
            "ज्यादा जुर्माना",
            "इन सभी का समावेश"
          ],
          correctAnswer: 3,
          explanation: "बार-बार उल्लंघन पर लाइसेंस निलंबन, कैद और बड़े जुर्माने हो सकते हैं।"
        },
        {
          id: 16,
          question: "सार्वजनिक जगहों पर रेसिंग या तेज गति के लिए दंड क्या है?",
          options: [
            "पहली बार ₹5,000 का जुर्माना",
            "केवल ₹200",
            "सामुदायिक सेवा",
            "कोई दंड नहीं"
          ],
          correctAnswer: 0,
          explanation: "रेसिंग/स्पीडिंग पर पहली बार ₹5,000 का जुर्माना लग सकता है।"
        },
        {
          id: 17,
          question: "बिना अनुमति वाहन के अनधिकृत उपयोग पर जुर्माना कितना है?",
          options: [
            "₹2,000",
            "₹5,000",
            "₹10,000",
            "₹500"
          ],
          correctAnswer: 2,
          explanation: "बिना अनुमति वाहन उपयोग पर ₹10,000 का जुर्माना है।"
        },
        {
          id: 18,
          question: "वाहन दस्तावेज न दिखाने पर जुर्माना कितना है?",
          options: [
            "₹100",
            "₹500",
            "₹200",
            "₹1,000"
          ],
          correctAnswer: 3,
          explanation: "दस्तावेज उपलब्ध न होने पर ₹1,000 का जुर्माना लगाया जाता है।"
        },
        {
          id: 19,
          question: "प्राइवेट कार में यात्रियों का अधिक लोडिंग करने पर जुर्माना कितना होगा?",
          options: [
            "प्रति अतिरिक्त व्यक्ति ₹500",
            "₹2,000 स्थिर",
            "कोई जुर्माना नहीं",
            "₹50"
          ],
          correctAnswer: 0,
          explanation: "अतिरिक्त प्रत्येक यात्री पर ₹500 का जुर्माना होता है।"
        },
        {
          id: 20,
          question: "दोपहिया वाहन चलाते हुए मोबाइल फोन पर बात करने पर जुर्माना कितना है?",
          options: [
            "₹1,000",
            "₹2,000",
            "₹10,000",
            "₹100"
          ],
          correctAnswer: 1,
          explanation: "मोबाइल फोन पर बात करने पर ₹2,000 का जुर्माना है।"
        }
      ]
    },
    {
      id: 7,
      title: "रास्ते का अधिकार",
      difficulty: "intermediate",
      icon: "➡️",
      totalQuestions: 20,
      questions: [
        {
          id: 1,
          question: "टी-जंक्शन पर रास्ते का अधिकार किसे मिलता है?",
          options: [
            "मुख्य सड़क पर वाहन",
            "दाईं ओर मुड़ने वाला वाहन",
            "साइड रोड वाला वाहन",
            "पैदल यात्री"
          ],
          correctAnswer: 0,
          explanation: "मुख्य सड़क पर ट्रैफ़िक को रास्ते का अधिकार होता है।"
        },
        {
          id: 2,
          question: "राउंडअबाउट में किसे प्राथमिकता देनी चाहिए?",
          options: [
            "राउंडअबाउट में प्रवेश कर रहे वाहन",
            "राउंडअबाउट में प्रतीक्षारत वाहन",
            "राउंडअबाउट में पहले से मौजूद वाहन",
            "कोई नहीं"
          ],
          correctAnswer: 2,
          explanation: "हमेशा पहले से राउंडअबाउट में मौजूद वाहनों को प्राथमिकता दें।"
        },
        {
          id: 3,
          question: "पैदल यात्री क्रॉसिंग पर किसे प्राथमिकता मिलती है?",
          options: [
            "कारों को",
            "पैदल यात्रियों को",
            "साइकिल चालकों को",
            "सभी को समान"
          ],
          correctAnswer: 1,
          explanation: "पैदल यात्रियों को रास्ते का अधिकार प्राप्त होता है।"
        },
        {
          id: 4,
          question: "स्लिप रोड से हाइवे में प्रवेश करते समय आपको क्या करना चाहिए?",
          options: [
            "हाइवे ट्रैफिक को रास्ता दें",
            "अन्य वाहनों के रुकने की उम्मीद करें",
            "बिना देखे जल्दी मर्ज करें",
            "मुख्य लेन में रुकें"
          ],
          correctAnswer: 0,
          explanation: "हाइवे पर ट्रैफिक को रास्ता देना चाहिए।"
        },
        {
          id: 5,
          question: "पहाड़ी सड़कों पर चढ़ाई और उतराई के दौरान किसे प्राथमिकता मिलती है?",
          options: [
            "नीचे उतर रहे वाहन को",
            "ऊपर चढ़ रहे वाहन को",
            "सभी वाहनों को समान",
            "किसी को नहीं"
          ],
          correctAnswer: 1,
          explanation: "ऊपर चढ़ रहे वाहन को रास्ते का अधिकार होता है।"
        },
        {
          id: 6,
          question: "यील्ड (Yield) साइन ड्राइवर को क्या निर्देश देता है?",
          options: [
            "गति बढ़ाएं",
            "अन्य वाहनों को रास्ता दें",
            "दूसरे वाहनों को रोकें",
            "कोई नहीं"
          ],
          correctAnswer: 1,
          explanation: "यील्ड का मतलब है दूसरों को पहले गुजरने देना।"
        },
        {
          id: 7,
          question: "पार्किंग स्थल से सड़क पर आने पर आपको क्या करना चाहिए?",
          options: [
            "आपको रास्ता मिलता है",
            "मुख्य ट्रैफिक को रास्ता दें",
            "सड़क पर पार्क करें",
            "हॉर्न बजाकर इंतजार करें"
          ],
          correctAnswer: 1,
          explanation: "मुख्य सड़क पर चल रहे ट्रैफिक को हमेशा प्राथमिकता दें।"
        },
        {
          id: 8,
          question: "आपातकालीन वाहन (सायरन के साथ) आने पर क्या करना चाहिए?",
          options: [
            "इग्नोर करें",
            "तुरंत रास्ता दें",
            "चुनौती दें",
            "उनका अनुसरण करें"
          ],
          correctAnswer: 1,
          explanation: "हमेशा आपातकालीन वाहनों को रास्ता दें।"
        },
        {
          id: 9,
          question: "बिना गेट वाली रेलवे क्रॉसिंग पर कौन सा वाहन प्राथमिकता रखता है?",
          options: [
            "रूके बिना ड्राइव करें",
            "ट्रेन को प्राथमिकता दें",
            "आपको प्राथमिकता है",
            "कोई नहीं"
          ],
          correctAnswer: 1,
          explanation: "रेलगोाड़ी को हमेशा रास्ते का अधिकार होता है।"
        },
        {
          id: 10,
          question: "जब दो वाहन एक ही समय पर चौराहे पर पहुंचें, तो कौन पहले जाता है?",
          options: [
            "सबसे बायां वाहन पहले जाए",
            "सबसे बड़ा वाहन पहले जाए",
            "जो वाहन दाईं ओर हो, वह पहले जाए",
            "जो पहले संकेत दे, वह जाए"
          ],
          correctAnswer: 2,
          explanation: "भारत में दाईं ओर वाला वाहन प्राथमिकता पाता है।"
        },
        {
          id: 11,
          question: "संकरा पुल पर रास्ते का अधिकार किसे मिलता है?",
          options: [
            "पुल के निकटतम वाहन को",
            "केवल भारी वाहनों को",
            "बसों को",
            "सबसे तेज वाहन को"
          ],
          correctAnswer: 0,
          explanation: "पहले पहुँचने वाले वाहन को प्राथमिकता होती है।"
        },
        {
          id: 12,
          question: "असंसोधित चौराहे पर कौन पहले जाता है?",
          options: [
            "दाईं ओर वाहन",
            "बाईं ओर वाहन",
            "छोटा वाहन",
            "हमेशा पैदल यात्री"
          ],
          correctAnswer: 0,
          explanation: "दाईं ओर मौजूद वाहन को प्राथमिकता मिलती है।"
        },
        {
          id: 13,
          question: "अगर दो वाहन एक साथ राउंडअबाउट में प्रवेश करें, तो कौन जाएगा?",
          options: [
            "दाईं ओर वाहन",
            "बाईं ओर वाहन",
            "सबसे बड़ा वाहन",
            "जो तेज हो"
          ],
          correctAnswer: 0,
          explanation: "दाईं ओर वाहन को प्राथमिकता दी जाती है।"
        },
        {
          id: 14,
          question: "एकतरफा सड़क पर मर्ज करते समय क्या करना चाहिए?",
          options: [
            "हमेशा पहले मर्ज करें",
            "पहले से सड़क पर मौजूद वाहनों को रास्ता दें",
            "बीच में रुकें",
            "हॉर्न बजाकर घुस जाएं"
          ],
          correctAnswer: 1,
          explanation: "मुख्य सड़क पर पहले से मौजूद वाहनों को रास्ता देना चाहिए।"
        },
        {
          id: 15,
          question: "ओवरटेक करते समय ओवरटेक किए जा रहे वाहन को क्या करना चाहिए?",
          options: [
            "गति बढ़ाए",
            "स्थिर गति बनाए रखें",
            "तुरंत रुकें",
            "होर्न बजाकर चेतावनी दें"
          ],
          correctAnswer: 1,
          explanation: "सुरक्षित ओवरटेकिंग के लिए वाहन को स्थिर गति बनानी चाहिए।"
        },
        {
          id: 16,
          question: "स्लिप रोड पर मर्ज करते वाहन को क्या करना चाहिए?",
          options: [
            "रास्ते का अधिकार होता है",
            "ट्रैफिक में खाली जगह का इंतजार करें",
            "दूसरों को ब्लॉक करें",
            "चलती लेन में रुकें"
          ],
          correctAnswer: 1,
          explanation: "सुरक्षित दूरी और खाली जगह का इंतजार करके मर्ज करें।"
        },
        {
          id: 17,
          question: "अगर आप बाएं मुड़ रहे हैं और पैदल यात्री पार कर रहा है तो किसे रास्ता देना चाहिए?",
          options: [
            "पैदल यात्री को इंतजार करना चाहिए",
            "ड्राइवर को रास्ता देना चाहिए",
            "दोनों एक साथ गुजरें",
            "कोई नहीं"
          ],
          correctAnswer: 1,
          explanation: "ड्राइवर को हमेशा पैदल यात्री को रास्ता देना चाहिए।"
        },
        {
          id: 18,
          question: "मुख्य चौराहे पर दायें मुड़ते समय रास्ते का अधिकार किसे मिलता है?",
          options: [
            "आमने सामने सीधा जाने वाले वाहन को",
            "दायें मुड़ने वाले वाहन को",
            "साइकिल चालक को",
            "कोई नहीं"
          ],
          correctAnswer: 0,
          explanation: "आमने से सीधे आ रहे वाहनों को रास्ते का अधिकार होता है।"
        },
        {
          id: 19,
          question: "जब ट्रैफिक लाइट्स काम नहीं कर रही हों, तो आपको क्या करना चाहिए?",
          options: [
            "अन्य वाहनों को अनदेखा करें",
            "पहले आने वाले पहले जाएं",
            "हाथ के इशारे या प्राथमिकता नियमों का पालन करें",
            "हॉर्न बजाकर आगे बढ़ें"
          ],
          correctAnswer: 2,
          explanation: "हाथ के इशारों और रास्ते के अधिकार के नियमों का पालन करें।"
        },
        {
          id: 20,
          question: "पार्किंग स्थल से बाहर निकलते समय किसे प्राथमिकता देनी चाहिए?",
          options: [
            "पहले निकलने वाले को",
            "داخل हो रहे वाहनों को",
            "हॉर्न बजाकर निकलें",
            "रास्ता रोककर इंतजार करें"
          ],
          correctAnswer: 1,
          explanation: "पार्किंग स्थल से निकलते समय आने वाले वाहनों को रास्ता देना चाहिए।"
        }
      ]
    },
    {
      id: 8,
      title: "सुरक्षित ड्राइविंग प्रथाएं",
      difficulty: "intermediate",
      icon: "🛡️",
      totalQuestions: 20,
      questions: [
        {
          id: 1,
          question: "बारिश में ड्राइविंग करते समय आपको क्या करना चाहिए?",
          options: [
            "सिर्फ हाई बीम का उपयोग करें",
            "धीरे चलें और फॉलो करने की दूरी बढ़ाएं",
            "लाइट बंद कर दें",
            "गति बढ़ाएं"
          ],
          correctAnswer: 1,
          explanation: "बारिश में दूरी बढ़ाएं और धीरे चलें।"
        },
        {
          id: 2,
          question: "कार में मोबाइल फोन का सबसे सुरक्षित उपयोग तरीका क्या है?",
          options: [
            "सिग्नल पर",
            "केवल हैंड-फ्री सिस्टम का उपयोग करें और तभी जब गाड़ी पार्क हो",
            "धीमी गति से चलते वक्त",
            "तेज गति से"
          ],
          correctAnswer: 1,
          explanation: "फोन का उपयोग केवल तब करें जब गाड़ी ठहरी हो।"
        },
        {
          id: 3,
          question: "टायर के प्रेशर की जांच कब करनी चाहिए?",
          options: [
            "ऐच्छिक है",
            "नियमित रूप से होनी चाहिए",
            "सिर्फ पंचर होने पर",
            "लंबे सफर के बाद ही"
          ],
          correctAnswer: 1,
          explanation: "सुरक्षा के लिए टायर प्रेशर नियमित जांच करनी चाहिए।"
        },
        {
          id: 4,
          question: "'दो सेकंड नियम' किसके लिए इस्तेमाल होता है?",
          options: [
            "ईंधन बचाने के लिए",
            "आगे वाली कार से सुरक्षित दूरी बनाए रखने के लिए",
            "गति नापने के लिए",
            "हॉर्न उपयोग के लिए"
          ],
          correctAnswer: 1,
          explanation: "आगे वाहन के पीछे कम से कम 2 सेकंड की दूरी रखें।"
        },
        {
          id: 5,
          question: "दर्पण (मिरर) कब समायोजित करना चाहिए?",
          options: [
            "सिर्फ रात को",
            "यात्रा शुरू करने से पहले",
            "पार्किंग के बाद",
            "जरूरत नहीं"
          ],
          correctAnswer: 1,
          explanation: "ड्राइविंग शुरू करने से पहले मिरर सेट करें।"
        },
        {
          id: 6,
          question: "ओवरटेकिंग करते समय आपको क्या करना चाहिए?",
          options: [
            "मिरर और ब्लाइंड स्पॉट देखें",
            "हॉर्न बजाएं और तेजी से बढ़ें",
            "बाएं से ओवरटेक करें",
            "बिलकुल पास रहें"
          ],
          correctAnswer: 0,
          explanation: "ओवरटेक करते समय सभी तरफ से सावधानीपूर्वक जांच करें।"
        },
        {
          id: 7,
          question: "बड़े वाहन के पीछे ड्राइव करते समय क्या करना चाहिए?",
          options: [
            "बहुत करीबी से फॉलो करें",
            "उसके ब्लाइंड स्पॉट से दूर रहें और दूरी बनाए रखें",
            "लगातार हॉर्न बजाएं",
            "कोई नहीं"
          ],
          correctAnswer: 1,
          explanation: "बड़े वाहन के ब्लाइंड स्पॉट बड़े होते हैं, इसलिए दूरी रखें।"
        },
        {
          id: 8,
          question: "लंबे सफर में जब आप थक जाएं तो क्या करना चाहिए?",
          options: [
            "अगले तक चलें",
            "रुककर आराम करें",
            "जल्दी पहुंचने के लिए तेज चलाएं",
            "सिर्फ खिड़कियां खोलें"
          ],
          correctAnswer: 1,
          explanation: "थकावट में ड्राइविंग न करें, आराम करें।"
        },
        {
          id: 9,
          question: "कम दृश्यता (लो विजिबिलिटी) में सबसे सही तरीका क्या है?",
          options: [
            "मध्यम लेन में पार्क करें",
            "फॉग लैंप या लो बीम हेडलाइट का उपयोग करें",
            "हाई बीम का उपयोग करें",
            "लाइट बंद करें"
          ],
          correctAnswer: 1,
          explanation: "कम दृश्यता में लो बीम या फॉग लैंप का प्रयोग करें।"
        },
        {
          id: 10,
          question: "अगर अचानक पैदल यात्री सामने आ जाए, तो आपको क्या करना चाहिए?",
          options: [
            "सिर्फ हॉर्न बजाएं",
            "धीमी करें या रुकें",
            "गति बढ़ाएं",
            "नज़रअंदाज करें"
          ],
          correctAnswer: 1,
          explanation: "पैदल यात्री के लिए हमेशा रुकने के लिए तैयार रहें।"
        },
        {
          id: 11,
          question: "ऊपर की ढलान पर पार्किंग करते समय क्या करना चाहिए?",
          options: [
            "पहिए कर्ब से दूर करें और हैंडब्रेक लगाएं",
            "हैंडब्रेक न लगाएं",
            "इंजन चलते रहने दें",
            "पहिए कर्ब की ओर करें"
          ],
          correctAnswer: 0,
          explanation: "पहिया कर्ब से दूर और हैंडब्रेक लगाएं ताकि वाहन पीछे न गिरे।"
        },
        {
          id: 12,
          question: "हॉर्न का उपयोग कब नहीं करना चाहिए?",
          options: [
            "अस्पताल और स्कूल के पास",
            "हाइवे पर",
            "पुल पर",
            "क्रॉसिंग पर"
          ],
          correctAnswer: 0,
          explanation: "संवेदनशील इलाकों में हॉर्न बजाना टालें।"
        },
        {
          id: 13,
          question: "सड़क पर गीले पत्ते:",
          options: [
            "सतह की पकड़ बढ़ाते हैं",
            "बर्फ जितने फिसलनदार हो सकते हैं",
            "कोई प्रभाव नहीं",
            "ब्रेकिंग में मदद करते हैं"
          ],
          correctAnswer: 1,
          explanation: "गीले पत्ते बर्फ जितने फिसलनादार हो सकते हैं।"
        },
        {
          id: 14,
          question: "अगर फुटपाथ से आपका गाड़ी के नीचे गेंद गिरती है, तो आपको क्या करना चाहिए?",
          options: [
            "नज़रअंदाज करें",
            "मालूम करें कि कोई बच्चा पीछे आ सकता है",
            "गति बढ़ाएं",
            "रिवर्स लें"
          ],
          correctAnswer: 1,
          explanation: "गेंद गिरने पर बच्चे के आने की संभावना होती है, सावधानी बरतें।"
        },
        {
          id: 15,
          question: "स्कूल जोन में एक रुकी हुई बस को पार करते समय क्या करना चाहिए?",
          options: [
            "गति बढ़ाएं",
            "धीमी करें और पार करते हुए बच्चों की नजर रखें",
            "नज़रअंदाज करें",
            "हॉर्न बजाएं"
          ],
          correctAnswer: 1,
          explanation: "रुकी बस के पास बच्चों पर ध्यान दें।"
        },
        {
          id: 16,
          question: "कम टायर प्रेशर का एक कारण क्या हो सकता है?",
          options: [
            "बेहतर पकड़",
            "स्टीयरिंग भारी होना",
            "क्षमता और सुरक्षा में कमी",
            "कोई प्रभाव नहीं"
          ],
          correctAnswer: 2,
          explanation: "कम प्रेशर से रोलिंग रेजिस्टेंस बढ़ता है और सुरक्षा कम होती है।"
        },
        {
          id: 17,
          question: "जब भी आप लेन बदलते हैं तो क्या करना चाहिए?",
          options: [
            "बस लेन बदल दें",
            "सिग्नल दें और मिरर व ब्लाइंड स्पॉट देखें",
            "गति बढ़ाएं",
            "किसी को खबर देने की जरूरत नहीं"
          ],
          correctAnswer: 1,
          explanation: "हमेशा संकेत दें और ब्लाइंड स्पॉट चेक करें।"
        },
        {
          id: 18,
          question: "आवासीय क्षेत्र में गति सीमा कितनी होनी चाहिए?",
          options: [
            "25 किमी/घंटा से अधिक न हो",
            "तेज होनी चाहिए",
            "कोई फर्क नहीं पड़ता",
            "हाइवे जितनी तेज हो"
          ],
          correctAnswer: 0,
          explanation: "आवासीय क्षेत्र में गति 25 किमी/घंटा से कम रखें।"
        },
        {
          id: 19,
          question: "लंबी यात्रा में क्या व्यवहार अनुशंसित नहीं है?",
          options: [
            "नियमित ब्रेक लेना",
            "थका-थका कर या नींद में ड्राइव करना",
            "हल्का भोजन करना",
            "रास्ते की योजना बनाना"
          ],
          correctAnswer: 1,
          explanation: "थका-थका या नींद में कभी भी ड्राइव न करें।"
        },
        {
          id: 20,
          question: "फ्यूज फटना किस चीज का कारण बन सकता है?",
          options: [
            "ब्रेक फेल होना",
            "कोई असर नहीं",
            "इलेक्ट्रिकल सिस्टम का फेल होना (लाइट्स/हॉर्न/इंडिकेटर)",
            "इंजन फेल होना"
          ],
          correctAnswer: 2,
          explanation: "फ्यूज फटने से इलेक्ट्रिक सिस्टम जैसे लाइट और हॉर्न बंद हो सकते हैं।"
        }
      ]
    },
    {
      id: 9,
      title: "उन्नत चालक",
      difficulty: "hard",
      icon: "🏆",
      totalQuestions: 20,
      questions: [
        {
          id: 1,
          question: "भारतीय कानून के अनुसार, निजी कार चालकों के लिए अधिकतम अनुमत रक्त शराब स्तर (BAC) क्या है?",
          options: [
            "0.03%",
            "0.05%",
            "0.08%",
            "शून्य सहिष्णुता"
          ],
          correctAnswer: 0,
          explanation: "निजी वाहनों के लिए कानूनी सीमा 0.03% BAC है।"
        },
        {
          id: 2,
          question: "मोटर व्हीकल एक्ट की धारा 185 किससे संबंधित है?",
          options: [
            "गति सीमा उल्लंघन",
            "शराब पीकर ड्राइविंग",
            "वाहनों का पंजीकरण",
            "हेलमेट नियम"
          ],
          correctAnswer: 1,
          explanation: "धारा 185 शराब पीकर ड्राइविंग की सजा से संबंधित है।"
        },
        {
          id: 3,
          question: "लगातार उल्लंघनों के कारण वाहन का पंजीकरण अधिकतम कितने समय तक निलंबित किया जा सकता है?",
          options: [
            "1 महीना",
            "3 महीने",
            "6 महीने",
            "1 वर्ष"
          ],
          correctAnswer: 2,
          explanation: "वाहन का पंजीकरण 6 महीने तक निलंबित किया जा सकता है।"
        },
        {
          id: 4,
          question: "निजी वाहन पंजीकरण प्रमाणपत्र की वैधता कितनी होती है?",
          options: [
            "10 वर्ष",
            "15 वर्ष",
            "20 वर्ष",
            "5 वर्ष"
          ],
          correctAnswer: 1,
          explanation: "निजी वाहनों के लिए पंजीकरण 15 वर्ष के लिए होता है।"
        },
        {
          id: 5,
          question: "संकरा पुल (नैरो ब्रिज) के चिन्ह पर चालक को क्या करना चाहिए?",
          options: [
            "हमेशा रुकें",
            "धीरे और सावधानी से आगे बढ़ें",
            "गति बढ़ाएं",
            "रिवर्स लें"
          ],
          correctAnswer: 1,
          explanation: "गति कम करें और आवश्यकतानुसार रास्ता दें।"
        },
        {
          id: 6,
          question: "सड़क के बीच में लगातार पीली लाइन का क्या मतलब होता है?",
          options: [
            "ओवरटेकिंग की अनुमति है",
            "ओवरटेकिंग या पार न करें",
            "पार्किंग क्षेत्र",
            "सिर्फ रुकें"
          ],
          correctAnswer: 1,
          explanation: "ओवरटेकिंग या लेन क्रॉसिंग मना है।"
        },
        {
          id: 7,
          question: "PUC (पॉल्यूशन अंडर कंट्रोल सर्टिफिकेट) किन वाहनों के लिए आवश्यक है?",
          options: [
            "सभी वाहनों के लिए",
            "सिर्फ दोपहिया",
            "सिर्फ वाणिज्यिक वाहन",
            "कोई नहीं"
          ],
          correctAnswer: 0,
          explanation: "सभी वाहनों के लिए प्रदूषण नियंत्रण प्रमाणपत्र अनिवार्य है।"
        },
        {
          id: 8,
          question: "ABS (एंटी-लॉक ब्रेकिंग सिस्टम) का मुख्य उद्देश्य क्या है?",
          options: [
            "ब्रेकिंग के दौरान स्थिरता बनाए रखना",
            "गति बढ़ाना",
            "बेहतर माइलेज",
            "प्रदूषण कम करना"
          ],
          correctAnswer: 0,
          explanation: "ABS पहियों को लॉक होने से रोकता है और स्टीयरिंग नियंत्रण में मदद करता है।"
        },
        {
          id: 9,
          question: "एकतरफा सड़क उल्लंघन पर जुर्माना कितना है?",
          options: [
            "₹100",
            "₹500",
            "₹2,000",
            "₹10,000"
          ],
          correctAnswer: 1,
          explanation: "एकतरफा नियम उल्लंघन पर ₹500 का जुर्माना है।"
        },
        {
          id: 10,
          question: "शहरी क्षेत्र में अधिकतम गति सीमा क्या है (जब अन्यथा सांकेत न हो)?",
          options: [
            "25 किमी/घंटा",
            "50 किमी/घंटा",
            "60 किमी/घंटा",
            "80 किमी/घंटा"
          ],
          correctAnswer: 1,
          explanation: "शहरी क्षेत्र में सामान्य अधिकतम गति 50 किमी/घंटा है।"
        },
        {
          id: 11,
          question: "वाणिज्यिक वाहनों के लिए वाहन फिटनेस प्रमाण पत्र कौन जारी करता है?",
          options: [
            "आरटीओ",
            "डीलर",
            "बीमा कंपनी",
            "वाहन का मालिक"
          ],
          correctAnswer: 0,
          explanation: "आरटीओ वाहन फिटनेस प्रमाण पत्र जारी करता है।"
        },
        {
          id: 12,
          question: "नए निजी वाहन का पंजीकरण कब नवीनीकृत होता है?",
          options: [
            "10 वर्ष के बाद",
            "15 वर्ष के बाद",
            "5 वर्ष के बाद",
            "20 वर्ष के बाद"
          ],
          correctAnswer: 1,
          explanation: "RC 15 वर्ष बाद नवीनीकृत होता है।"
        },
        {
          id: 13,
          question: "डीजल वाहनों से निकलने वाला काला धुआं क्या संकेत करता है?",
          options: [
            "इंजन कुशल है",
            "अधूरा दहन / प्रदूषण",
            "कोई समस्या नहीं",
            "पेट्रोल चला रहा है"
          ],
          correctAnswer: 1,
          explanation: "काला धुआं अधूरे दहन और प्रदूषण का संकेत है।"
        },
        {
          id: 14,
          question: "रुकने की दूरी किन कारकों से बढ़ती है?",
          options: [
            "गति बढ़ना",
            "पुराने टायर",
            "गीली सड़क",
            "ऊपर के सभी"
          ],
          correctAnswer: 3,
          explanation: "इन सभी कारणों से रुकने की दूरी बढ़ती है।"
        },
        {
          id: 15,
          question: "हाइड्रोकार्बन उत्सर्जन मुख्यतः किस कारण होता है?",
          options: [
            "इंजन में दहन",
            "पेंटिंग",
            "टायर घर्षण",
            "एयर कंडीशनिंग"
          ],
          correctAnswer: 0,
          explanation: "इंजन में दहन प्रदूषण का मुख्य स्रोत है।"
        },
        {
          id: 16,
          question: "भारी वाहनों का 'ब्लाइंड स्पॉट' क्या होता है?",
          options: [
            "सिर्फ वाहन के पीछे",
            "ऐसा क्षेत्र जो मिरर में नहीं दिखता",
            "डैशबोर्ड पर",
            "स्टीयरिंग के बगल में"
          ],
          correctAnswer: 1,
          explanation: "ब्लाइंड स्पॉट वे क्षेत्र हैं जो ड्राइवर को सीधे नहीं दिखते।"
        },
        {
          id: 17,
          question: "वाहन मालिक की जिम्मेदारी क्या होती है?",
          options: [
            "सिर्फ मैकेनिकल दोष",
            "कानूनी अनुपालन और बीमा",
            "कोई नहीं",
            "सफर की योजना बनाना"
          ],
          correctAnswer: 1,
          explanation: "मालिक को वाहन का कानूनी और बीमा होना सुनिश्चित करना होता है।"
        },
        {
          id: 18,
          question: "सड़क के बीच में डबल सॉलिड लाइन का क्या अर्थ है?",
          options: [
            "दोनों तरफ से ओवरटेकिंग/पार करना मना है",
            "सिर्फ रात में ओवरटेकिंग करें",
            "साइकिल लेन",
            "कोई असर नहीं"
          ],
          correctAnswer: 0,
          explanation: "डबल सॉलिड लाइन पर किसी भी तरफ से पार या ओवरटेकिंग मना है।"
        },
        {
          id: 19,
          question: "एयरबैग किस स्थिति में सुरक्षा प्रदान करता है?",
          options: [
            "इंजन फेल होने पर",
            "दुर्घटना (फ्रंटल टक्कर) में",
            "बारिश में",
            "रिवर्स करते समय"
          ],
          correctAnswer: 1,
          explanation: "एयरबैग टक्कर के दौरान सुरक्षा प्रदान करता है।"
        },
        {
          id: 20,
          question: "वाहन कब जब्त किया जा सकता है?",
          options: [
            "लाइसेंस अवैध होने पर",
            "बीमा समाप्त होने पर",
            "प्रदूषण प्रमाणपत्र न होने पर",
            "उपर्युक्त सभी स्थितियों में"
          ],
          correctAnswer: 3,
          explanation: "इन सभी उल्लंघनों पर वाहन जब्त किया जा सकता है।"
        }
      ]
    },
    {
      id: 10,
      title: "विशेषज्ञ चुनौती",
      difficulty: "hard",
      icon: "🚗💨",
      totalQuestions: 20,
      questions: [
        {
          id: 1,
          question: "एक्वाप्लेनिंग (पानी पर फिसलना) से सही तरह कैसे निपटें?",
          options: [
            "कठोर ब्रेक लगाएं",
            "एक्सेलेरेटर से धीरे करें, जिस दिशा में जाना हो उसी तरफ स्टीयर करें",
            "गति बढ़ाएं",
            "इग्निशन बंद करें"
          ],
          correctAnswer: 1,
          explanation: "एक्वाप्लेनिंग के दौरान एक्सेलेरेटर को धीरे करें और स्टीयरिंग हल्के से करें।"
        },
        {
          id: 2,
          question: "मोटर व्हीकल एक्ट की धारा 184 किसका दंड निर्धारित करती है?",
          options: [
            "शराब पीकर ड्राइविंग",
            "खतरनाक ड्राइविंग (जिसमें ओवरस्पीडिंग शामिल है)",
            "बिना बीमा ड्राइविंग",
            "बिना हेलमेट सवारी"
          ],
          correctAnswer: 1,
          explanation: "धारा 184 में लापरवाह और खतरनाक ड्राइविंग की सजा दी गई है।"
        },
        {
          id: 3,
          question: "वाणिज्यिक वाहनों के लिए फिटनेस सर्टिफिकेट कितनी बार नवीनीकृत किया जाना चाहिए?",
          options: [
            "40 वर्ष की आयु के बाद 5 वर्षों पर",
            "प्रत्येक वर्ष",
            "6 महीने में एक बार",
            "20 वर्षों में एक बार"
          ],
          correctAnswer: 1,
          explanation: "वाणिज्यिक वाहनों के लिए फिटनेस सर्टिफिकेट हर साल नवीनीकरण आवश्यक है।"
        },
        {
          id: 4,
          question: "भारत में लर्निंग लाइसेंस की वैधता कितनी होती है?",
          options: [
            "3 महीने",
            "6 महीने",
            "1 साल",
            "5 साल"
          ],
          correctAnswer: 1,
          explanation: "लर्निंग लाइसेंस 6 महीने के लिए मान्य होता है।"
        },
        {
          id: 5,
          question: "नए वाहन का पंजीकरण करने का सही क्रम क्या है?",
          options: [
            "बीमा प्राप्त करें → वाहन पंजीकरण करें → प्रदूषण प्रमाणपत्र लें",
            "बीमा लें → प्रदूषण प्रमाणपत्र लें → पंजीकरण करें",
            "कर अदायगी करें → ड्राइविंग लाइसेंस लें",
            "उपरोक्त में से कोई नहीं"
          ],
          correctAnswer: 0,
          explanation: "पहले बीमा करें, फिर पंजीकरण के लिए आवेदन दें और अंत में प्रदूषण प्रमाणपत्र लें।"
        },
        {
          id: 6,
          question: "धारा 184 के तहत खतरनाक ड्राइविंग में कौन शामिल नहीं है?",
          options: [
            "ओवरस्पीडिंग",
            "ड्राइविंग करते हुए सेलफोन का उपयोग",
            "बिना वजह हॉर्न बजाना",
            "गलत लेन में ड्राइविंग"
          ],
          correctAnswer: 2,
          explanation: "अत्यधिक हॉर्न बजाना सीधे खतरनाक ड्राइविंग के अंतर्गत नहीं आता।"
        },
        {
          id: 7,
          question: "एक्सप्रेसवे पर कार की गति सीमा (यदि संकेत न हो तो) कितनी होती है?",
          options: [
            "60 किमी/घंटा",
            "100 किमी/घंटा",
            "120 किमी/घंटा",
            "80 किमी/घंटा"
          ],
          correctAnswer: 1,
          explanation: "अधिकांश एक्सप्रेसवे पर अधिकतम गति 100 किमी/घंटा होती है।"
        },
        {
          id: 8,
          question: "ट्रैफ़िक सिग्नल पर 'एम्बर' (पीली) लाइट का अर्थ क्या है?",
          options: [
            "जाएँ",
            "चेतावनी/रुकने के लिए तैयार रहें",
            "गति बढ़ाएँ",
            "सिर्फ मुड़ें"
          ],
          correctAnswer: 1,
          explanation: "पीली लाइट का मतलब है सावधान रहें और रुकने के लिए तैयार हो जाएं।"
        },
        {
          id: 9,
          question: "हॉर्न कब उपयोग किया जाना चाहिए?",
          options: [
            "दूसरे सड़क उपयोगकर्ताओं को खतरे से चेताने के लिए",
            "साइलेंट जोन में",
            "सिग्नल पर हमेशा",
            "मज़े के लिए"
          ],
          correctAnswer: 0,
          explanation: "हॉर्न केवल खतरनाक स्थिति में चेतावनी देने के लिए प्रयोग करें।"
        },
        {
          id: 10,
          question: "प्रभावी रुकने की दूरी किस पर निर्भर करती है?",
          options: [
            "सड़क की स्थिति और वाहन की गति",
            "कार का रंग",
            "ईंधन स्तर",
            "सीट कवर का प्रकार"
          ],
          correctAnswer: 0,
          explanation: "रुकने की दूरी सड़क की स्थिति और गति से प्रभावित होती है।"
        },
        {
          id: 11,
          question: "हेडलाइट को कब डिम (कम तेज) करना चाहिए?",
          options: [
            "ओवरटेक करते समय",
            "जब कोई दूसरी गाड़ी सामने आये",
            "सुरंग में",
            "कभी नहीं"
          ],
          correctAnswer: 1,
          explanation: "आगामी गाड़ियों को अंधा करने से बचाने के लिए लाइट डिम करें।"
        },
        {
          id: 12,
          question: "एयरबैग के सुरक्षित संचालन के लिए क्या जांचना चाहिए?",
          options: [
            "टायर प्रेशर",
            "सीट बेल्ट का उपयोग",
            "इंजन का तेल",
            "एयर कंडीशनिंग"
          ],
          correctAnswer: 1,
          explanation: "एयरबैग तब सबसे बेहतर काम करता है जब सीट बेल्ट लगाई गई हो।"
        },
        {
          id: 13,
          question: "ट्रैफिक में लगातार हॉर्न बजाने के क्या परिणाम हो सकते हैं?",
          options: [
            "दुर्घटनाएं",
            "ध्यान भटकना",
            "शोर प्रदूषण",
            "इन सभी"
          ],
          correctAnswer: 3,
          explanation: "लगातार हॉर्न बजाना शोर प्रदूषण पैदा करता है और दुर्घटना का कारण बन सकता है।"
        },
        {
          id: 14,
          question: "हाइवे पर ओवरटेकिंग के लिए किस लेन का उपयोग करना चाहिए?",
          options: [
            "बायाँ",
            "सबसे दाहिना लेन",
            "मध्य",
            "कोई भी"
          ],
          correctAnswer: 1,
          explanation: "ओवरटेकिंग हमेशा सबसे दाहिने लेन से करें।"
        },
        {
          id: 15,
          question: "क्या लर्नर लाइसेंस के साथ आपको परिवहन वाहन चला सकते हैं?",
          options: [
            "हाँ",
            "नहीं",
            "मालिक की अनुमति से हाँ",
            "आपातकाल में"
          ],
          correctAnswer: 1,
          explanation: "लर्नर लाइसेंस परिवहन वाहन चलाने के लिए वैध नहीं है।"
        },
        {
          id: 16,
          question: "गीली सड़क पर फिसलने का मुख्य कारण क्या है?",
          options: [
            "कम दबाव वाले टायर",
            "अचानक तेज गति या ब्रेक लगाना",
            "साफ विंडशील्ड",
            "भारी वाहन"
          ],
          correctAnswer: 1,
          explanation: "अचानक तेज ब्रेक या एक्सेलेरेशन से फिसलन होती है।"
        },
        {
          id: 17,
          question: "ट्रक के माल भरे कम्पार्टमेंट में यात्रा करना:",
          options: [
            "अनुमत है",
            "अनुमत नहीं है",
            "रात्रि में अनुमत",
            "सुरक्षित है"
          ],
          correctAnswer: 1,
          explanation: "यह अवैध और असुरक्षित है।"
        },
        {
          id: 18,
          question: "अधिक बार ओवरस्पीडिंग करने पर क्या हो सकता है?",
          options: [
            "कोई समस्या नहीं",
            "ज्यादा जुर्माना और लाइसेंस निलंबन का खतरा",
            "सायरन लगाना",
            "पुलिस की प्रशंसा"
          ],
          correctAnswer: 1,
          explanation: "बार-बार उल्लंघन पर जुर्माना बढ़ता है और लाइसेंस निलंबित हो सकता है।"
        },
        {
          id: 19,
          question: "साफ मौसम में फॉग लैंप का गलत उपयोग क्या कर सकता है?",
          options: [
            "बेहतर दृष्टि",
            "दूसरों के लिए चकाचौंध और ध्यान भटकना",
            "तेज ड्राइविंग",
            "कम जुर्माना"
          ],
          correctAnswer: 1,
          explanation: "फॉग लैंप की गलत चमक से सामने वाले चालक की आँखें घायल हो सकती हैं।"
        },
        {
          id: 20,
          question: "भारतीय नियमों के अनुसार वाहन की अधिकतम अनुमति प्राप्त चौड़ाई क्या है?",
          options: [
            "1.8 मीटर",
            "2.6 मीटर",
            "3.2 मीटर",
            "3.8 मीटर"
          ],
          correctAnswer: 1,
          explanation: "अधिकतम अनुमत वाहन चौड़ाई 2.6 मीटर है।"
        }
      ]
    }
  ]
};


const DLTest = () => {
  const [selectedLanguage, setSelectedLanguage] = useState('english');
  const [selectedCollection, setSelectedCollection] = useState(null);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [showExplanation, setShowExplanation] = useState(false);
  const [score, setScore] = useState(0);
  const [completedQuestions, setCompletedQuestions] = useState([]);
  const [userData, setUserData] = useState(null);
  const [showCertificate, setShowCertificate] = useState(false);
  const [certificateId] = useState(() => `VB-DL-${new Date().getFullYear()}-${Math.random().toString(36).substring(2, 8).toUpperCase()}`);
  const certificateRef = useRef(null);

  // Fetch user data on component mount
  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const token = localStorage.getItem('token');
        const userId = localStorage.getItem('user_id');
        console.log('Fetching user data - token exists:', !!token, 'userId:', userId);
        if (token && userId) {
          const response = await fetch(`https://api.rtoxpert.com/api/users/profile/${userId}`, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          });
          const result = await response.json();
          console.log('User data response:', result);
          if (response.ok && result.user) {
            setUserData(result.user);
            console.log('User data set:', result.user.name, result.user.profile_picture);
          }
        } else {
          console.log('No token or userId found in localStorage');
        }
      } catch (error) {
        console.error('Error fetching user data:', error);
      }
    };
    fetchUserData();
  }, []);

  const saveCertificateToBackend = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        console.log('No token found, skipping certificate save');
        return;
      }

      const percentage = Math.round((score / currentCollection.questions.length) * 100);
      const testLanguage = selectedLanguage === 'english' ? 'DL Test - English' : 'DL Test - Hindi';

      const response = await fetch('https://api.rtoxpert.com/api/certificates', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          test_type: testLanguage,
          test_title: currentCollection.title,
          score: score,
          total_questions: currentCollection.questions.length,
          percentage: percentage
        })
      });

      const result = await response.json();
      if (response.ok) {
        console.log('Certificate saved successfully:', result);
      } else {
        console.error('Failed to save certificate:', result.message);
      }
    } catch (error) {
      console.error('Error saving certificate to backend:', error);
    }
  };

  const handleDownloadCertificate = async () => {
    try {
      // Import html2canvas dynamically
      const html2canvas = (await import('html2canvas')).default;

      if (certificateRef.current) {
        // Ensure all images are loaded before capturing
        const images = certificateRef.current.getElementsByTagName('img');
        const imageLoadPromises = Array.from(images).map(img => {
          if (img.complete) return Promise.resolve();
          return new Promise((resolve, reject) => {
            img.onload = resolve;
            img.onerror = resolve; // Resolve even if error to not block the process
          });
        });

        await Promise.all(imageLoadPromises);

        // Wait a bit for fonts and styles to fully render
        await new Promise(resolve => setTimeout(resolve, 500));

        const canvas = await html2canvas(certificateRef.current, {
          scale: 3, // Increased scale for better quality
          backgroundColor: '#f5f7fa',
          logging: false,
          useCORS: true, // Enable cross-origin images
          allowTaint: true, // Allow tainted canvas
          windowWidth: certificateRef.current.scrollWidth,
          windowHeight: certificateRef.current.scrollHeight,
          scrollX: 0,
          scrollY: 0,
          // Force text rendering
          onclone: (clonedDoc) => {
            const clonedElement = clonedDoc.querySelector('[class*="certificateScoreBox"]');
            if (clonedElement) {
              // Ensure text is visible by forcing styles
              const scoreLabel = clonedElement.querySelector('[class*="certificateScoreLabel"]');
              const scoreValue = clonedElement.querySelector('[class*="certificateScore"]');
              if (scoreLabel) {
                scoreLabel.style.color = '#FFFFFF';
                scoreLabel.style.opacity = '1';
                scoreLabel.style.visibility = 'visible';
              }
              if (scoreValue) {
                scoreValue.style.color = '#FFFFFF';
                scoreValue.style.opacity = '1';
                scoreValue.style.visibility = 'visible';
              }
            }
          }
        });

        const image = canvas.toDataURL('image/png', 1.0); // Max quality
        const link = document.createElement('a');
        link.download = `VaahanBuddy_Certificate_${new Date().getTime()}.png`;
        link.href = image;
        link.click();

        // Save certificate to backend
        await saveCertificateToBackend();
      }
    } catch (error) {
      console.error('Error downloading certificate:', error);
      alert('Failed to download certificate. Please try again.');
    }
  };

  const handleShareCertificate = async () => {
    try {
      const percentage = Math.round((score / currentCollection.questions.length) * 100);
      const shareText = `🎉 I completed my DL Test Preparation on VaahanBuddy!\n\n📊 Score: ${score}/${currentCollection.questions.length} (${percentage}%)\n\n🚗 Download VaahanBuddy and ace your driving test:\nhttps://play.google.com/store/apps/details?id=com.dealer.VaahanBuddy&pcampaignid=web_share`;

      if (navigator.share) {
        await navigator.share({
          title: 'My DL Test Certificate',
          text: shareText,
        });
      } else {
        navigator.clipboard.writeText(shareText);
        alert('Share message copied to clipboard!');
      }

      // Save certificate to backend
      await saveCertificateToBackend();
    } catch (error) {
      console.error('Error sharing certificate:', error);
    }
  };

  const currentCollection = testCollections[selectedLanguage]?.find(c => c.id === selectedCollection);
  const currentQuestion = currentCollection?.questions[currentQuestionIndex];

  // Calculate passing status
  const isPass = currentCollection
    ? score / currentCollection.questions.length >= 0.7
    : false;

  const handleLanguageChange = (language) => {
    setSelectedLanguage(language);
    setSelectedCollection(null);
    resetQuiz();
  };

  const handleCollectionSelect = (collectionId) => {
    setSelectedCollection(collectionId);
    resetQuiz();
  };

  const resetQuiz = () => {
    setCurrentQuestionIndex(0);
    setSelectedAnswer(null);
    setShowExplanation(false);
    setScore(0);
    setCompletedQuestions([]);
  };

  const handleAnswerSelect = (answerIndex) => {
    if (showExplanation) return;
    setSelectedAnswer(answerIndex);
    setShowExplanation(true);
    if (answerIndex === currentQuestion.correctAnswer) {
      setScore(score + 1);
    }
    setCompletedQuestions([...completedQuestions, {
      questionId: currentQuestion.id,
      selectedAnswer: answerIndex,
      correct: answerIndex === currentQuestion.correctAnswer
    }]);
  };

  const handleNextQuestion = () => {
    if (currentQuestionIndex < currentCollection.questions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
      setSelectedAnswer(null);
      setShowExplanation(false);
    }
  };

  const getDifficultyClass = (difficulty) => {
    return styles[difficulty.toLowerCase()];
  };

  const getOptionClass = (index) => {
    if (!showExplanation) {
      return selectedAnswer === index ? styles.selected : '';
    }
    if (index === currentQuestion.correctAnswer) {
      return styles.correct;
    }
    if (selectedAnswer === index && selectedAnswer !== currentQuestion.correctAnswer) {
      return styles.incorrect;
    }
    return styles.disabled;
  };

  const getOptionIcon = (index) => {
    if (!showExplanation) return null;
    if (index === currentQuestion.correctAnswer) {
      return <CheckCircle className={styles.optionIcon} />;
    }
    if (selectedAnswer === index && selectedAnswer !== currentQuestion.correctAnswer) {
      return <XCircle className={styles.optionIcon} />;
    }
    return null;
  };

  if (!selectedCollection) {
    return (
      <div className={styles.container}>
        <div className={styles.header}>
          <h1 className={styles.title}>
            {selectedLanguage === 'english' ? 'Driving License Test' : 'ड्राइविंग लाइसेंस टेस्ट'}
          </h1>
          <p className={styles.subtitle}>
            {selectedLanguage === 'english'
              ? 'Practice for your driving license exam with our comprehensive test collections'
              : 'हमारे व्यापक टेस्ट संग्रह के साथ अपनी ड्राइविंग लाइसेंस परीक्षा का अभ्यास करें'}
          </p>
        </div>

        <div className={styles.languageSection}>
          <h2 className={styles.languageTitle}>
            {selectedLanguage === 'english' ? 'Select Language' : 'भाषा चुनें'}
          </h2>
          <div className={styles.languageButtons}>
            <button
              onClick={() => handleLanguageChange('english')}
              className={`${styles.languageButton} ${selectedLanguage === 'english' ? styles.active : styles.inactive}`}
            >
              English
            </button>
            <button
              onClick={() => handleLanguageChange('hindi')}
              className={`${styles.languageButton} ${selectedLanguage === 'hindi' ? styles.active : styles.inactive}`}
            >
              हिंदी
            </button>
          </div>
        </div>

        <div className={styles.collectionsGrid}>
          {testCollections[selectedLanguage]?.map((collection) => (
            <div
              key={collection.id}
              onClick={() => handleCollectionSelect(collection.id)}
              className={styles.collectionCard}
            >
              <div className={styles.collectionHeader}>
                <div className={styles.collectionIcon}>{collection.icon}</div>
                <span className={`${styles.difficultyBadge} ${getDifficultyClass(collection.difficulty)}`}>
                  {collection.difficulty}
                </span>
              </div>

              <h3 className={styles.collectionTitle}>
                {collection.title}
              </h3>

              <div className={styles.collectionStats}>
                <div className={styles.collectionStat}>
                  <BookOpen className={styles.optionIcon} />
                  <span>{collection.totalQuestions} {selectedLanguage === 'english' ? 'Questions' : 'प्रश्न'}</span>
                </div>
                <div className={styles.collectionStat}>
                  <Clock className={styles.optionIcon} />
                  <span>{Math.ceil(collection.totalQuestions * 1.5)} {selectedLanguage === 'english' ? 'min' : 'मिनट'}</span>
                </div>
              </div>

              <div className={styles.collectionFooter}>
                <span className={styles.startButton}>
                  {selectedLanguage === 'english' ? 'Start Test' : 'टेस्ट शुरू करें'}
                </span>
                <ChevronRight className={`${styles.optionIcon} ${styles.startButton}`} />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.quizContainer}>
        <div className={styles.quizHeader}>
          <div className={styles.quizHeaderTop}>
            <button
              onClick={() => setSelectedCollection(null)}
              className={styles.backButton}
            >
              ← {selectedLanguage === 'english' ? 'Back to Collections' : 'संग्रह पर वापस जाएं'}
            </button>
            <span className={`${styles.difficultyBadge} ${getDifficultyClass(currentCollection.difficulty)}`}>
              {currentCollection.difficulty}
            </span>
          </div>

          <h1 className={styles.quizTitle}>
            {currentCollection.title}
          </h1>

          <div className={styles.quizStats}>
            <div className={styles.quizStatsLeft}>
              <span>
                {selectedLanguage === 'english' ? 'Question' : 'प्रश्न'} {currentQuestionIndex + 1} {selectedLanguage === 'english' ? 'of' : 'का'} {currentCollection.questions.length}
              </span>
              <span>
                {selectedLanguage === 'english' ? 'Score:' : 'स्कोर:'} {score}/{completedQuestions.length}
              </span>
            </div>
            <div className={styles.progressBar}>
              <div
                className={styles.progressFill}
                style={{ width: `${((currentQuestionIndex + 1) / currentCollection.questions.length) * 100}%` }}
              ></div>
            </div>
          </div>
        </div>

        <div className={styles.questionCard}>
          <h2 className={styles.questionText}>
            {currentQuestion?.question}
          </h2>

          <div className={styles.optionsList}>
            {currentQuestion?.options.map((option, index) => (
              <div
                key={index}
                onClick={() => handleAnswerSelect(index)}
                className={`${styles.optionItem} ${getOptionClass(index)}`}
              >
                <div className={styles.optionContent}>
                  <div className={`${styles.optionRadio} ${selectedAnswer === index ? styles.selected : ''}`}>
                    {selectedAnswer === index && !showExplanation && (
                      <div className={styles.optionRadioDot}></div>
                    )}
                  </div>
                  <span className={styles.optionText}>{option}</span>
                </div>
                {getOptionIcon(index)}
              </div>
            ))}
          </div>
        </div>

        {showExplanation && (
          <div className={styles.explanationCard}>
            <div className={styles.explanationHeader}>
              <div className={`${styles.explanationIcon} ${selectedAnswer === currentQuestion.correctAnswer ? styles.correct : styles.incorrect}`}>
                {selectedAnswer === currentQuestion.correctAnswer ? (
                  <CheckCircle className={styles.optionIcon} />
                ) : (
                  <XCircle className={styles.optionIcon} />
                )}
              </div>
              <div className={styles.explanationContent}>
                <h3 className={selectedAnswer === currentQuestion.correctAnswer ? styles.correct : styles.incorrect}>
                  {selectedLanguage === 'english' ? (selectedAnswer === currentQuestion.correctAnswer ? 'Correct!' : 'Incorrect') : (selectedAnswer === currentQuestion.correctAnswer ? 'सही!' : 'गलत')}
                </h3>
                <p className={styles.explanationText}>
                  {currentQuestion.explanation}
                </p>
              </div>
            </div>
          </div>
        )}

        {showExplanation && (
          <div className={styles.nextButtonContainer}>
            {currentQuestionIndex < currentCollection.questions.length - 1 ? (
              <button
                onClick={handleNextQuestion}
                className={styles.nextButton}
              >
                {selectedLanguage === 'english' ? 'Next Question' : 'अगला प्रश्न'}
                <ChevronRight className={styles.optionIcon} />
              </button>
            ) : (
              <div className={styles.completionCard}>
                <div className={styles.completionIcon}>
                  <Award />
                </div>
                <h2 className={styles.completionTitle}>
                  {selectedLanguage === 'english' ? 'Test Completed!' : 'टेस्ट पूरा हुआ!'}
                </h2>
                <p className={styles.completionScore}>
                  {selectedLanguage === 'english' ? 'Your final score:' : 'आपका अंतिम स्कोर:'} {score}/{currentCollection.questions.length} ({Math.round((score / currentCollection.questions.length) * 100)}%)
                </p>
                <p className={isPass ? styles.pass : styles.fail}>
                  {isPass
                    ? (selectedLanguage === 'english' ? 'Status: PASS' : 'स्थिति: पास')
                    : (selectedLanguage === 'english' ? 'Status: FAIL' : 'स्थिति: फेल')}
                </p>

                {/* Certificate Action Buttons */}
                <div className={styles.certificateActions}>
                  <button
                    onClick={() => setShowCertificate(!showCertificate)}
                    className={styles.certificateButton}
                  >
                    {showCertificate
                      ? (selectedLanguage === 'english' ? 'Hide Certificate' : 'प्रमाणपत्र छुपाएं')
                      : (selectedLanguage === 'english' ? '📜 View Certificate' : '📜 प्रमाणपत्र देखें')
                    }
                  </button>
                  {showCertificate && (
                    <>
                      <button
                        onClick={handleDownloadCertificate}
                        className={styles.downloadButton}
                      >
                        <Download size={20} />
                        {selectedLanguage === 'english' ? 'Download' : 'डाउनलोड'}
                      </button>
                      <button
                        onClick={handleShareCertificate}
                        className={styles.shareButton}
                      >
                        <Share2 size={20} />
                        {selectedLanguage === 'english' ? 'Share' : 'साझा करें'}
                      </button>
                    </>
                  )}
                </div>

                {/* Certificate Display */}
                {showCertificate && (
                  <div ref={certificateRef} className={styles.certificateContainer}>
                    <div className={styles.certificate}>
                      {/* Decorative Corner Elements */}
                      <div className={styles.certificateCorner} style={{ top: 0, left: 0 }}></div>
                      <div className={styles.certificateCorner} style={{ top: 0, right: 0, transform: 'rotate(90deg)' }}></div>
                      <div className={styles.certificateCorner} style={{ bottom: 0, left: 0, transform: 'rotate(-90deg)' }}></div>
                      <div className={styles.certificateCorner} style={{ bottom: 0, right: 0, transform: 'rotate(180deg)' }}></div>

                      {/* Header with Logo */}
                      <div className={styles.certificateLogoSection}>
                        <img
                          src={logoImage}
                          alt="VaahanBuddy Logo"
                          className={styles.certificateLogoImage}
                        />
                        {/* <div className={styles.certificateBrandName}>VAAHANBUDDY</div> */}
                        <div className={styles.certificateTagline}>Your Trusted Vehicle Companion</div>
                      </div>

                      {/* Ornamental Divider */}
                      <div className={styles.certificateOrnament}>
                        <span className={styles.ornamentLine}></span>
                        <span className={styles.ornamentStar}>★</span>
                        <span className={styles.ornamentLine}></span>
                      </div>

                      {/* Certificate Title */}
                      <div className={styles.certificateHeader}>Certificate of Achievement</div>
                      <div className={styles.certificateSubHeader}>This is to proudly certify that</div>

                      {/* User Section */}
                      <div className={styles.certificateUserSection}>
                        <img
                          src={userData?.profile_picture || 'https://upload.wikimedia.org/wikipedia/commons/7/7c/Profile_avatar_placeholder_large.png'}
                          alt="Profile"
                          className={styles.certificateUserImage}
                          onError={(e) => {
                            e.target.src = 'https://upload.wikimedia.org/wikipedia/commons/7/7c/Profile_avatar_placeholder_large.png';
                          }}
                          crossOrigin="anonymous"
                        />
                        <div className={styles.certificateName}>{userData?.name || 'VaahanBuddy User'}</div>
                      </div>

                      {/* Achievement Description */}
                      <div className={styles.certificateAchievement}>
                        has successfully completed the
                      </div>
                      <div className={styles.certificateTestTitle}>
                        Driving License Preparation Test
                      </div>
                      <div className={styles.certificateAchievement}>
                        and demonstrated excellent knowledge of Indian traffic rules, road safety regulations, and driving best practices.
                      </div>

                      {/* Test Details Grid */}
                      <div className={styles.certificateDetailsGrid}>
                        <div className={styles.certificateDetailItem}>
                          <span className={styles.detailLabel}>Test Category</span>
                          <span className={styles.detailValue}>{currentCollection.title}</span>
                        </div>
                        <div className={styles.certificateDetailItem}>
                          <span className={styles.detailLabel}>Difficulty Level</span>
                          <span className={styles.detailValue}>
                            {currentCollection.difficulty === 'easy' && '🟢 '}
                            {currentCollection.difficulty === 'intermediate' && '🟡 '}
                            {currentCollection.difficulty === 'hard' && '🔴 '}
                            {currentCollection.difficulty.charAt(0).toUpperCase() + currentCollection.difficulty.slice(1)}
                          </span>
                        </div>
                        <div className={styles.certificateDetailItem}>
                          <span className={styles.detailLabel}>Total Questions</span>
                          <span className={styles.detailValue}>{currentCollection.questions.length}</span>
                        </div>
                        <div className={styles.certificateDetailItem}>
                          <span className={styles.detailLabel}>Status</span>
                          <span className={`${styles.detailValue} ${isPass ? styles.statusPass : styles.statusFail}`}>
                            {isPass ? '✓ PASSED' : '✗ FAILED'}
                          </span>
                        </div>
                      </div>

                      {/* Score Box */}
                      <div className={styles.certificateScoreBox}>
                        <span className={styles.certificateScoreLabel}>Final Score</span>
                        <span className={styles.certificateScore}>
                          {score} / {currentCollection.questions.length}
                        </span>
                        <span className={styles.certificatePercentage}>
                          {Math.round((score / currentCollection.questions.length) * 100)}%
                        </span>
                      </div>

                      {/* Date and Certificate ID */}
                      <div className={styles.certificateMeta}>
                        <div className={styles.certificateDate}>
                          <strong>Date of Completion</strong>
                          <span>{new Date().toLocaleDateString('en-IN', {
                            weekday: 'long',
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric'
                          })}</span>
                        </div>
                        <div className={styles.certificateIdBox}>
                          <strong>Certificate ID</strong>
                          <span>{certificateId}</span>
                        </div>
                      </div>

                      {/* Signature Section */}
                      <div className={styles.certificateSignatureSection}>
                        <div className={styles.signatureBox}>
                          <div className={styles.signatureLine}></div>
                          <span className={styles.signatureLabel}>Digital Verification</span>
                        </div>
                        <div className={styles.sealContainer}>
                          <div className={styles.officialSeal}>
                            <span className={styles.sealInner}>✓</span>
                          </div>
                          <span className={styles.sealText}>Verified</span>
                        </div>
                        <div className={styles.signatureBox}>
                          <div className={styles.signatureLine}></div>
                          <span className={styles.signatureLabel}>Authorized by VaahanBuddy</span>
                        </div>
                      </div>

                      {/* Footer */}
                      <div className={styles.certificateFooter}>
                        <a
                          href="https://play.google.com/store/apps/details?id=com.dealer.VaahanBuddy"
                          target="_blank"
                          rel="noopener noreferrer"
                          className={styles.certificateAppPromo}
                          style={{ textDecoration: 'none' }}
                        >
                          <span className={styles.promoIcon}>📱</span>
                          <div className={styles.promoText}>
                            <strong>Download VaahanBuddy App</strong>
                            <span>Your complete vehicle management solution</span>
                          </div>
                        </a>
                        <div className={styles.certificateWebsite}>
                          www.vaahanbuddy.com
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                <button
                  onClick={() => setSelectedCollection(null)}
                  className={styles.restartButton}
                >
                  {selectedLanguage === 'english' ? 'Try Another Test' : 'दूसरा टेस्ट करें'}
                </button>
              </div>
            )}

          </div>
        )}
      </div>
    </div>
  );
};

export default DLTest;